<template>
    <div class="posiciones-abiertas-container">
      <!-- Alerts Section (Grouped & Collapsible) -->
      <div v-if="alertsLoading" class="loading-indicator-gcn">Cargando alertas...</div>
      <div v-if="alertsError" class="error-message-gcn">{{ alertsError }}</div>
  
      <div v-if="Object.keys(groupedAlerts).length > 0" class="alerts-section">
          <div class="alerts-header">
              <h3>Alertas Pendientes</h3>
          </div>
  
          <!-- Outer loop for alert groups -->
          <div v-for="(groupAlerts, typeName) in groupedAlerts" :key="typeName" class="alert-group" :class="[getAlertTypeClass(groupAlerts[0].type), { 'is-collapsed': alertGroupCollapsedState[typeName] }]">
              <h4 @click="toggleAlertGroup(typeName)" class="collapsible-header">
                  <div class="header-content">
                    <span class="collapse-icon">{{ alertGroupCollapsedState[typeName] ? '▶' : '▼' }}</span>
                    <span class="alert-group-icon material-icons-outlined">notifications</span>
                    {{ typeName }} ({{ groupAlerts.length }})
                  </div>
                  <div class="bulk-actions alert-group-bulk-actions" @click.stop>
                      <select v-model="alertGroupBulkActions[typeName]" :disabled="getSelectedAlertsInGroup(groupAlerts).length === 0">
                          <option value="" disabled>Acciones</option>
                          <option value="ignore">Ignorar Sel.</option>
                          <option v-if="groupAlerts.length > 0 && groupAlerts[0].can_it_be_solved_automatically" value="apply_solution">Aplicar Sol. Sel.</option>
                      </select>
                      <button class="btnGcn btnGcn-secondary btnGcn-sm" @click="applyBulkAlertActionForGroup(groupAlerts, typeName)" :disabled="getSelectedAlertsInGroup(groupAlerts).length === 0 || !alertGroupBulkActions[typeName]">
                          Aplicar
                      </button>
                  </div>
              </h4>
              <div v-show="!alertGroupCollapsedState[typeName]" class="alert-group-content">
                  <table class="alert-table">
                      <thead>
                          <tr>
                              <th><input type="checkbox" :checked="areAllAlertsInGroupSelected(groupAlerts)" @change="toggleSelectAllAlertsInGroup($event, groupAlerts)" title="Seleccionar/Deseleccionar todo en este grupo"></th>
                              <th>Ticker</th>
                              <th>Detalles</th>
                              <th style="width: 220px;">Acciones</th>
                          </tr>
                      </thead>
                      <tbody>
                          <tr v-for="alert in groupAlerts" :key="alert.id" class="alert-item">
                              <td><input type="checkbox" :value="alert.id" v-model="selectedAlerts"></td>
                              <td>{{ alert.ticker }}</td>
                              <td>{{ alert.data.message }}</td>
                              <td class="alert-actions">
                                  <!-- Report error is now only an individual action -->
                                  <button v-if="alert.can_it_report_error && !alert.error_reported" class="btnGcn btnGcn-sm btnGcn-warning" @click="reportAlertError(alert)" title="Reportar Error">Reportar</button>
                                  <button v-if="alert.can_it_be_solved_automatically" class="btnGcn btnGcn-sm btnGcn-success" @click="applyAlertSolution(alert)" title="Aplicar Cambios">Aplicar</button>
                                  <button class="btnGcn btnGcn-sm btnGcn-secondary" @click="ignoreAlert(alert)" title="Ignorar Alerta">Ignorar</button>
                              </td>
                          </tr>
                      </tbody>
                  </table>
              </div>
          </div>
           <p v-if="bulkAlertError" class="error-message-gcn small" style="margin-top: 10px;">{{ bulkAlertError }}</p>
      </div>

      <!-- List Actions for Open Positions -->
      <div class="list-actions">
         <div class="list-actions-left">
             <button class="btnGcn custom-btnGcn" @click="openImportModal">
                 <span class="material-icons-outlined">file_upload</span> Importar Posición Existente
             </button>
             <!-- Column Customizer -->
             <TableColumnCustomizer 
                 :columns="defaultColumns" 
                 storage-key="posicionesAbiertas"
                 @columns-changed="handleColumnsChanged"
             />
         </div>
         <div class="list-actions-right">
             <!-- Bulk Actions -->
             <div class="bulk-actions position-bulk-actions">
                 <button class="btnGcn btnGcn-secondary btnGcn-sm" @click="applyBulkAction" :disabled="selectedPositions.length === 0">
                     Eliminar seleccionados
                 </button>
             </div>
         </div>
      </div>
  
      <!-- Loading/Error for Open Positions List -->
      <div v-if="loading" class="loading-indicator-gcn">Cargando posiciones abiertas...</div>
      <div v-if="error" class="error-message-gcn">{{ error }}</div>
  
      <!-- Open Positions Table -->
      <div class="table-responsive" v-if="!loading && !error && openPositions.length > 0">
          <table class="table data-table open-positions-table">
               <thead>
                  <tr>
                       <th><input type="checkbox" @change="toggleSelectAll" :checked="allSelected"></th>
                       <th 
                           v-for="column in visibleColumns" 
                           :key="column.key"
                           :class="{ 'sortable-header': column.sortable !== false, 'number-cell': column.numeric }"
                           @click="column.sortable !== false ? sortByColumn(column.key) : null"
                       >
                         {{ column.label }}
                         <span v-if="column.sortable !== false" :class="getSortIconClass(column.key)"></span>
                       </th>
                      <th>Opciones</th>
                  </tr>
              </thead>
               <tbody>
                  <tr v-for="pos in sortedOpenPositions" :key="pos.id">
                      <td><input type="checkbox" :value="pos.id" v-model="selectedPositions"></td>
                       <td 
                           v-for="column in visibleColumns" 
                           :key="column.key"
                           :class="getCellClass(column, pos)"
                       >
                           <!-- Ticker Cell Special Rendering -->
                           <template v-if="column.key === 'ticker'">
                               <div class="ticker-content-wrapper" :title="generateTickerTooltip(pos)">
                                   <div class="ticker-left">
                                       <b>{{ pos.ticker }}</b>
                                       <small>{{ pos.company_name }}</small>
                                   </div>
                                   <div class="ticker-right">
                                       <span v-if="!isExternalPosition(pos)"
                                             class="direction-icon material-icons-outlined"
                                             :class="pos.direction == 1 ? 'bullish' : 'bearish'" 
                                             :title="pos.direction == 1 ? 'Alcista' : 'Bajista'">
                                           {{ pos.direction == 1 ? 'trending_up' : 'trending_down' }}
                                       </span>
                                       <span v-else class="external-badge" title="No Eurekers">NO EUREKERS</span>
                                       <span class="instrument-icon material-icons-outlined" 
                                             :title="pos.instrument_type === 'STOCK' ? 'Acción' : 'CFD'">
                                           {{ pos.instrument_type === 'STOCK' ? 'show_chart' : 'candlestick_chart' }}
                                       </span>
                                       <small class="currency">{{ pos.currency || '-' }}</small>
                                   </div>
                               </div>
                           </template>
                           <!-- Entry Price Cell Special Rendering -->
                           <template v-else-if="column.key === 'in_price'">
                               <div>
                                   {{ formatPrice(pos.in_price, pos.stock_decimals) }}
                                   <br v-if="!isExternalPosition(pos)">
                                   <small v-if="!isExternalPosition(pos)" class="current-profitability" :class="getProfitabilityClass(pos)">
                                       Rentabilidad actual:<br>
                                       {{ calculateCurrentProfitability(pos) }}
                                   </small>
                               </div>
                           </template>
                           <!-- Out Price Cell Special Rendering -->
                           <template v-else-if="column.key === 'out_price'">
                               {{ formatPrice(pos.out_price, pos.stock_decimals) }}
                           </template>
                           <!-- Current Price Cell Special Rendering -->
                           <template v-else-if="column.key === 'current_price'">
                               {{ formatPrice(pos.current_price, pos.stock_decimals) }}{{ getCurrencySymbol(pos.currency) }}
                           </template>
                           <!-- Notes Cell Special Rendering -->
                           <template v-else-if="column.key === 'notes'">
                               <span v-if="!pos.editingNotes">{{ pos.notes || '-' }}</span>
                               <textarea v-else class="form-control-gcn form-control-gcn-sm" 
                                         :data-id="pos.id" 
                                         v-model="pos.editNotesValue"></textarea>
                               <button v-if="!pos.editingNotes" class="btnGcn btnGcn-link btnGcn-sm" 
                                       @click="startEditNotes(pos)" title="Editar Notas">
                                   <span class="material-icons-outlined">edit</span>
                               </button>
                               <button v-else class="btnGcn btnGcn-link btnGcn-sm" 
                                       @click="saveNotes(pos)" title="Guardar Notas">
                                   <span class="material-icons-outlined">save</span>
                               </button>
                           </template>
                           <!-- Default Cell Rendering -->
                           <template v-else>
                               <span v-html="renderCell(pos, column)"></span>
                           </template>
                       </td>
                       <td class="options-cell">
                          <button class="btnGcn btnGcn-link btnGcn-sm btnGcn-graph" @click="openDynamicChart(pos.ticker)" title="Graficador Dinámico"><span class="material-icons-outlined">bar_chart</span></button>
                          <button class="btnGcn btnGcn-link btnGcn-sm btnGcn-graph" @click="openStockChart(pos)" title="Gráfico Entrada"><span class="material-icons-outlined">show_chart</span></button>
                          <a v-if="pos.options && pos.options.forum && pos.options.forum.url" :href="pos.options.forum.url" target="_blank" class="btnGcn btnGcn-link btnGcn-sm" :title="pos.options.forum.title || 'Ver foro'"><span class="material-icons-outlined">forum</span></a>
                          <span v-else class="btnGcn btnGcn-link btnGcn-sm disabled" title="Foro no disponible"><span class="material-icons-outlined">forum</span></span>
                          <button class="btnGcn btnGcn-link btnGcn-sm" @click="openEditModal(pos)" title="Editar Posición"><span class="material-icons-outlined">edit</span></button>
                          <button class="btnGcn btnGcn-link btnGcn-sm btnGcn-warning" @click="openCloseModal(pos)" title="Cerrar Posición"><span class="material-icons-outlined">highlight_off</span></button>
                          <button class="btnGcn btnGcn-link btnGcn-sm btnGcn-danger" @click="confirmDelete(pos.id)" title="Eliminar (Sin cerrar)"><span class="material-icons-outlined">delete</span></button>
                      </td>
                  </tr>
              </tbody>
          </table>
      </div>
       <div v-else-if="!loading && !error && openPositions.length === 0">
          <p>No hay posiciones abiertas para esta cartera.</p>
      </div>
  
      <!-- Modals (Keep Import and Close Modals) -->
       <Modal :show="showImportModal" title="Importar Posición Abierta Existente" @close="closeImportModal" size="lg">
           <template #body>
               <OpenPositionImportForm
                  ref="importForm" :portfolio-id="portfolioId" :is-submitting="isSubmitting"
                  :error="formError" @submit="handleImportPosition" @error="setFormError"
               />
           </template>
           <template #footer>
               <button class="btnGcn custom-btnGcn-outline" @click="closeImportModal">Cancelar</button>
               <button class="btnGcn custom-btnGcn" @click="$refs.importForm.submitForm()" :disabled="isSubmitting">
                   {{ isSubmitting ? 'Importando...' : 'Importar Posición' }}
               </button>
           </template>
       </Modal>
       <Modal :show="showCloseModal" title="Cerrar Posición" @close="closeCloseModal">
            <template #body>
               <div v-if="positionToClose">
                   <p>Cerrando posición: <strong>{{ positionToClose.ticker }}</strong> ({{ positionToClose.instrument_number }} unidades)</p>
                   <div v-if="closeFormError" class="error-message-gcn">{{ closeFormError }}</div>
                   <form @submit.prevent="handleClosePosition" ref="closeFormElement">
                       <!-- Close form fields -->
                        <div class="form-group-gcn"><label for="close-out-price">Precio de Cierre</label><input type="number" id="close-out-price" :step="priceStep(positionToClose.stock_decimals)" required class="form-control-gcn" v-model.number="closeFormData.out_price"><small>Precio por unidad en moneda del instrumento ({{ positionToClose.currency }})</small></div>
                        <div class="form-group-gcn">
                            <label for="close-out-currency">Tipo Cambio Cierre (Opcional)</label>
                            <input type="number" id="close-out-currency" step="any" min="0" class="form-control-gcn" v-model.number="closeFormData.out_currency" :disabled="exchangeRateLoading">
                            <button 
                                type="button"
                                class="btnGcn btnGcn-secondary btnGcn-sm exchange-rate-btn"
                                @click="fetchExchangeRateManually"
                                :disabled="exchangeRateLoading || !positionToClose">
                                <span v-if="exchangeRateLoading">🔄 Obteniendo...</span>
                                <span v-else>Obtener automáticamente tasa de cambio para hoy</span>
                            </button>
                            <small v-if="closeFormData.currency_pair && !exchangeRateLoading" class="exchange-rate-success">
                                ✅ Última tasa obtenida para {{ closeFormData.currency_pair }}
                            </small>
                            <small v-else-if="!exchangeRateLoading">
                                Tipo Instrumento/Cartera ({{ positionToClose.currency }}/{{displayPortfolioCurrency}}). Si se deja vacío o 0, se usa el cambio actual si está disponible, o 1.
                            </small>
                        </div>
                        <div class="form-group-gcn"><label for="close-out-date">Fecha Cierre</label><input type="date" id="close-out-date" required class="form-control-gcn" :value="formattedCloseDateForInput" @input="updateCloseDate"><small>Formato API: d-m-Y ({{ closeFormData.out_date }})</small></div>
                        <div class="form-group-gcn"><label for="close-close-fee">Comisión Cierre ({{ displayPortfolioCurrency }})</label><input type="number" id="close-close-fee" step="any" required min="0" class="form-control-gcn" v-model.number="closeFormData.close_fee"></div>
                        <input type="hidden" v-model="closeFormData.instrument_id"><button type="submit" style="display: none;"></button>
                   </form>
               </div>
           </template>
           <template #footer>
               <button class="btnGcn custom-btnGcn-outline" @click="closeCloseModal">Cancelar</button>
               <button class="btnGcn custom-btnGcn btnGcn-warning" @click="submitCloseForm" :disabled="isSubmitting">
                   {{ isSubmitting ? 'Cerrando...' : 'Confirmar Cierre' }}
               </button>
           </template>
       </Modal>
  
      <!-- Edit Modal -->
       <Modal :show="showEditModal" title="Editar Posición Abierta" @close="closeEditModal">
           <template #body>
               <div v-if="positionToEdit">
                   <p>Editando posición: <strong>{{ positionToEdit.ticker }}</strong></p>
                   <div v-if="editFormError" class="error-message-gcn">{{ editFormError }}</div>
                   <form @submit.prevent="handleUpdateOpenPosition" ref="editFormElement">

                       <!-- Mode Selection -->
                       <div class="edit-mode-selector">
                           <h4 class="edit-mode-title">¿Cómo calcular el precio de salida?</h4>
                           
                           <div class="edit-mode-options">
                               <!-- Automatic Mode -->
                               <label class="edit-mode-option" :class="{ 'active': followEurekersMethodology }">
                                   <input type="radio" :value="true" v-model="followEurekersMethodology" name="editMode" />
                                   <div class="mode-content">
                                       <div class="mode-text">
                                           <strong>Automático</strong>
                                           <small>Eurekers calcula el precio de salida</small>
                                       </div>
                                   </div>
                               </label>
                               
                               <!-- Manual Mode -->
                               <label class="edit-mode-option" :class="{ 'active': !followEurekersMethodology }">
                                   <input type="radio" :value="false" v-model="followEurekersMethodology" name="editMode" />
                                   <div class="mode-content">
                                       <div class="mode-text">
                                           <strong>Manual</strong>
                                           <small>Yo defino el precio de salida</small>
                                       </div>
                                   </div>
                               </label>
                           </div>
                       </div>

                       <!-- Instrument Number -->
                       <div class="form-group-gcn">
                           <label :for="'edit-instrument_number-' + positionToEdit.id" class="text-primary">
                               Acciones <span class="required">*</span>
                           </label>
                           <input 
                               type="text" 
                               :id="'edit-instrument_number-' + positionToEdit.id" 
                               class="form-control-gcn" 
                               :class="{ 'error': shouldShowEditError('instrument_number') }"
                               :value="formatEditDisplayValue(editFormData.instrument_number)"
                               @input="event => handleEditNumericInput(event, 'instrument_number')"
                               @blur="event => { markEditFieldTouched('instrument_number'); formatEditNumericOnBlur(event, 'instrument_number'); }"
                               @focus="event => { markEditFieldTouched('instrument_number'); handleEditFocus(event, 'instrument_number'); }"
                               required 
                               placeholder="0">
                           <small class="text-secondary">Número de acciones o unidades del instrumento</small>
                           <small v-if="shouldShowEditError('instrument_number')" class="error-message-gcn">{{ editInstrumentNumberError }}</small>
                       </div>
                       
                       <!-- Entry Price -->
                       <div class="form-group-gcn">
                           <label :for="'edit-in_price-' + positionToEdit.id" class="text-primary">
                               Precio de Entrada <span class="required">*</span>
                           </label>
                           <input 
                               type="text" 
                               :id="'edit-in_price-' + positionToEdit.id" 
                               class="form-control-gcn" 
                               :class="{ 'error': shouldShowEditError('in_price') }"
                               :value="formatEditDisplayValue(editFormData.in_price)"
                               @input="event => handleEditNumericInput(event, 'in_price')"
                               @blur="event => { markEditFieldTouched('in_price'); formatEditNumericOnBlur(event, 'in_price'); }"
                               @focus="event => { markEditFieldTouched('in_price'); handleEditFocus(event, 'in_price'); }"
                               required 
                               :placeholder="priceStep(positionToEdit.stock_decimals).toString()">
                           <small class="text-secondary">Precio por unidad en {{ positionToEdit.currency || 'moneda del instrumento' }}</small>
                           <small v-if="shouldShowEditError('in_price')" class="error-message-gcn">{{ editInPriceError }}</small>
                       </div>
                       
                       <!-- In Currency (Optional) -->
                       <div class="form-group-gcn">
                           <label :for="'edit-in_currency-' + positionToEdit.id" class="text-primary">
                               Valor Divisa Entrada
                           </label>
                           <input
                               type="text"
                               :id="'edit-in_currency-' + positionToEdit.id"
                               class="form-control-gcn"
                               :class="{ 'error': shouldShowEditError('in_currency') }"
                               :value="formatEditDisplayValue(editFormData.in_currency)"
                               @input="event => handleEditNumericInput(event, 'in_currency')"
                               @blur="event => { markEditFieldTouched('in_currency'); formatEditNumericOnBlur(event, 'in_currency'); }"
                               @focus="event => { markEditFieldTouched('in_currency'); handleEditFocus(event, 'in_currency'); }"
                               placeholder="0.00">
                           <small class="text-secondary">Tipo de cambio de entrada Instrumento/Cartera (ej. 1.0000 si igual divisa)</small>
                           <small v-if="shouldShowEditError('in_currency')" class="error-message-gcn">{{ editInCurrencyError }}</small>
                       </div>

                       <!-- Open Fee (Optional) -->
                       <div class="form-group-gcn">
                           <label :for="'edit-open_fee-' + positionToEdit.id" class="text-primary">
                               Comisión Apertura
                           </label>
                           <input
                               type="text"
                               :id="'edit-open_fee-' + positionToEdit.id"
                               class="form-control-gcn"
                               :class="{ 'error': shouldShowEditError('open_fee') }"
                               :value="formatEditDisplayValue(editFormData.open_fee)"
                               @input="event => handleEditNumericInput(event, 'open_fee')"
                               @blur="event => { markEditFieldTouched('open_fee'); formatEditNumericOnBlur(event, 'open_fee'); }"
                               @focus="event => { markEditFieldTouched('open_fee'); handleEditFocus(event, 'open_fee'); }"
                               placeholder="0.00">
                           <small class="text-secondary">Comisión de apertura en {{ displayPortfolioCurrency }}</small>
                           <small v-if="shouldShowEditError('open_fee')" class="error-message-gcn">{{ editOpenFeeError }}</small>
                       </div>

                       <!-- Out Price (Manual Mode Only) -->
                       <div v-if="editManualMode" class="form-group-gcn">
                           <label :for="'edit-out_price-' + positionToEdit.id" class="text-primary">
                               Precio de Salida <span class="required">*</span>
                           </label>
                           <input 
                               type="text" 
                               :id="'edit-out_price-' + positionToEdit.id" 
                               class="form-control-gcn" 
                               :class="{ 'error': shouldShowEditError('out_price') }"
                               :value="formatEditDisplayValue(editFormData.out_price)"
                               @input="event => handleEditNumericInput(event, 'out_price')"
                               @blur="event => { markEditFieldTouched('out_price'); formatEditNumericOnBlur(event, 'out_price'); }"
                               @focus="event => { markEditFieldTouched('out_price'); handleEditFocus(event, 'out_price'); }"
                               required 
                               :placeholder="priceStep(positionToEdit.stock_decimals).toString()">
                           <small class="text-secondary">Precio de salida objetivo en {{ positionToEdit.currency || 'moneda del instrumento' }}</small>
                           <small v-if="shouldShowEditError('out_price')" class="error-message-gcn">{{ editOutPriceError }}</small>
                       </div>

                       <!-- Support Price -->
                       <div class="form-group-gcn">
                           <label :for="'edit-support_price-' + positionToEdit.id" class="text-primary">
                               Precio de Soporte <span class="required">*</span>
                           </label>
                           <input 
                               type="text" 
                               :id="'edit-support_price-' + positionToEdit.id" 
                               class="form-control-gcn" 
                               :class="{ 'error': shouldShowEditError('support_price') }"
                               :value="formatEditDisplayValue(editFormData.support_price)"
                               @input="event => handleEditNumericInput(event, 'support_price')"
                               @blur="event => { markEditFieldTouched('support_price'); formatEditNumericOnBlur(event, 'support_price'); }"
                               @focus="event => { markEditFieldTouched('support_price'); handleEditFocus(event, 'support_price'); }"
                               required 
                               :placeholder="priceStep(positionToEdit.stock_decimals).toString()">
                           <small class="text-secondary">Precio de soporte técnico en {{ positionToEdit.currency || 'moneda del instrumento' }}</small>
                           <small v-if="shouldShowEditError('support_price')" class="error-message-gcn">{{ editSupportPriceError }}</small>
                       </div>
                       
                       <!-- Support Date -->
                       <div class="form-group-gcn">
                           <label :for="'edit-support_date-' + positionToEdit.id" class="text-primary">
                               Fecha de Soporte <span class="required">*</span>
                           </label>
                           <input 
                               type="date" 
                               :id="'edit-support_date-' + positionToEdit.id" 
                               class="form-control-gcn" 
                               :class="{ 'error': shouldShowEditError('support_date') }"
                               :value="formattedEditSupportDateForInput" 
                               @input="updateEditSupportDate"
                               @blur="markEditFieldTouched('support_date'); validateEditSupportDate()"
                               @focus="markEditFieldTouched('support_date')"
                               required>
                           <small class="text-secondary">Fecha en que se identificó el soporte (Formato API: {{ editFormData.support_date || 'd-m-Y' }})</small>
                           <small v-if="shouldShowEditError('support_date')" class="error-message-gcn">{{ editSupportDateError }}</small>
                       </div>

                       <!-- CFD Margin (Only for CFD instruments) -->
                       <div v-if="positionToEdit.instrument_type === 'CFD'" class="form-group-gcn">
                           <label :for="'edit-cfd_margin-' + positionToEdit.id" class="text-primary">
                               Margen CFD <span class="required">*</span>
                           </label>
                           <input 
                               type="text" 
                               :id="'edit-cfd_margin-' + positionToEdit.id" 
                               class="form-control-gcn" 
                               :class="{ 'error': shouldShowEditError('cfd_margin') }"
                               :value="formatEditDisplayValue(editFormData.cfd_margin)"
                               @input="event => handleEditNumericInput(event, 'cfd_margin')"
                               @blur="event => { markEditFieldTouched('cfd_margin'); formatEditNumericOnBlur(event, 'cfd_margin'); }"
                               @focus="event => { markEditFieldTouched('cfd_margin'); handleEditFocus(event, 'cfd_margin'); }"
                               required 
                               placeholder="0.00">
                           <small class="text-secondary">Margen CFD</small>
                           <small v-if="shouldShowEditError('cfd_margin')" class="error-message-gcn">{{ editCfdMarginError }}</small>
                       </div>

                       <!-- Form Status Indicator -->
                       <div v-if="isEditFormValid && editSubmitAttempted" class="form-status success">
                           <span class="status-icon">✓</span> Formulario completado correctamente
                       </div>
                       <div v-else-if="hasEditErrors && editSubmitAttempted" class="form-status error">
                           <span class="status-icon">⚠</span> Por favor, corrija los errores antes de continuar
                       </div>

                       <button type="submit" style="display: none;"></button>
                   </form>
               </div>
           </template>
           <template #footer>
               <button class="btnGcn custom-btnGcn-outline" @click="closeEditModal" :disabled="isSubmitting">Cancelar</button>
               <button class="btnGcn custom-btnGcn" @click="submitEditForm" :disabled="isSubmitting || (editSubmitAttempted && !isEditFormValid)">
                   {{ isSubmitting ? 'Guardando...' : 'Guardar Cambios' }}
               </button>
           </template>
       </Modal>
  
      <!-- Generic Confirmation Modal -->
      <Modal :show="showConfirmModal" :title="confirmModalTitle" @close="closeConfirmModal" size="sm">
          <template #body>
              <p>{{ confirmModalMessage }}</p>
              <!-- Error display within the confirmation modal -->
              <div v-if="formError && !closeFormError && !editFormError && !bulkAlertError" class="error-message-gcn">{{ formError }}</div>
          </template>
          <template #footer>
              <button class="btnGcn custom-btnGcn-outline" @click="closeConfirmModal" :disabled="isConfirmSubmitting">Cancelar</button>
              <button class="btnGcn custom-btnGcn" @click="executeConfirmAction" :disabled="isConfirmSubmitting">
                  {{ isConfirmSubmitting ? 'Confirmando...' : 'Confirmar' }}
              </button>
          </template>
      </Modal>

      <!-- Report Error Modal -->
      <Modal :show="showReportErrorModal" title="Reportar Error en Alerta" @close="closeReportErrorModal">
          <template #body>
              <div v-if="alertToReportError">
                  <p>Reportando error para alerta en ticker: <strong>{{ alertToReportError.ticker }}</strong></p>
                  <p><small>{{ alertToReportError.data.message }}</small></p>
                  <div v-if="reportErrorFormError" class="error-message-gcn">{{ reportErrorFormError }}</div>
                  <div class="form-group-gcn">
                      <label for="report-error-message-gcn">Mensaje adicional (opcional):</label>
                      <textarea id="report-error-message-gcn" class="form-control-gcn" v-model="reportErrorMessage" rows="3"></textarea>
                  </div>
              </div>
          </template>
          <template #footer>
              <button class="btnGcn custom-btnGcn-outline" @click="closeReportErrorModal" :disabled="reportErrorSubmitting">Cancelar</button>
              <button class="btnGcn custom-btnGcn" @click="handleReportErrorConfirmed" :disabled="reportErrorSubmitting">
                  {{ reportErrorSubmitting ? 'Reportando...' : 'Confirmar Reporte' }}
              </button>
          </template>
      </Modal>
  
    </div>
  </template>
  
  <script>
  import apiService, { formatDateDMY, parseDate, formatCurrency } from './apiService';
  import Modal from './Modal.vue'; // Adjust path if needed
  import OpenPositionImportForm from './OpenPositionImportForm.vue'; // Adjust path if needed
  import APICaller from "@/components/codeComponents/APICaller.js";
  import TableColumnCustomizer from '@/components/TableColumnCustomizer.vue';
  
  export default {
    name: 'PosicionesAbiertas',
    components: {
      Modal,
      OpenPositionImportForm,
      TableColumnCustomizer,
    },
    mixins: [APICaller],
    props: {
      portfolioId: { type: String, required: true },
    },
    data() {
      return {
        loading: false,
        error: null,
        openPositions: [],
        portfolioCurrency: 'EUR',
  
        // Alert data properties
        alertsLoading: false,
        alertsError: null,
        alerts: [],
  
        // Modals & Forms
        showImportModal: false,
        showCloseModal: false,
        showEditModal: false,
        formError: null, // For Import form
        closeFormError: null, // For Close form
        editFormError: null, // Added for Edit form
        isSubmitting: false,
        positionToClose: null,
        positionToEdit: null,
        closeFormData: {
            instrument_id: '',
            out_price: null,
            out_currency: null,
            currency_pair: '',
            out_date: formatDateDMY(new Date()),
            close_fee: 0,
        },
        exchangeRateLoading: false,
        editFormData: {
            instrument_number: null,
            in_price: null,
            out_price: null,
            support_price: null,
            support_date: '',
            cfd_margin: null,
            in_currency: null,
            open_fee: null,
        },

        // Edit Form Validation
        editInstrumentNumberError: null,
        editInPriceError: null,
        editOutPriceError: null,
        editSupportPriceError: null,
        editSupportDateError: null,
        editCfdMarginError: null,
        editInCurrencyError: null,
        editOpenFeeError: null,
        editInstrumentNumberValid: false,
        editInPriceValid: false,
        editOutPriceValid: false,
        editSupportPriceValid: false,
        editSupportDateValid: false,
        editCfdMarginValid: false,
        editInCurrencyValid: true,
        editOpenFeeValid: true,
        editTouchedFields: {
            instrument_number: false,
            in_price: false,
            out_price: false,
            support_price: false,
            support_date: false,
            cfd_margin: false,
            in_currency: false,
            open_fee: false
        },
        editSubmitAttempted: false,
  
        // Bulk Actions
        selectedPositions: [],

        // Alert Bulk Actions
        selectedAlerts: [],
        selectedBulkAlertAction: '',
        bulkAlertError: null,

        // Alert Group Bulk Actions - separate state per group
        alertGroupBulkActions: {},

        // Alert Group Collapse State
        alertGroupCollapsedState: {},

        // Report Error Modal State
        showReportErrorModal: false,
        alertToReportError: null,
        reportErrorMessage: '',
        reportErrorSubmitting: false,
        reportErrorFormError: null,

        // Generic Confirmation Modal State
        showConfirmModal: false,
        confirmModalTitle: '',
        confirmModalMessage: '',
        confirmModalAction: null,
        confirmModalData: null,
        isConfirmSubmitting: false,

        // Sorting state
        sortBy: 'ticker',
        sortDirection: 'asc', // 'asc' or 'desc'
        
        // Manual edit mode for open positions
        editManualMode: false,

        // Column customization
        activeColumns: [],
        defaultColumns: [
          { key: 'ticker', label: 'Ticker', visible: true, mandatory: true, numeric: false },
          { key: 'risk', label: 'Riesgo', visible: true, mandatory: true, numeric: true },
          { key: 'instrument_number', label: 'Acciones', visible: true, mandatory: true, numeric: true },
          { key: 'in_price', label: 'P.\nEntrada', visible: true, mandatory: true, numeric: true },
          { key: 'out_price', label: 'P.\nSalida', visible: true, mandatory: true, numeric: true },
          { key: 'current_price', label: 'P.\nActual', visible: true, mandatory: false, numeric: true },
          { key: 'support_price', label: 'P.\nSoporte', visible: true, mandatory: true, numeric: true },
          { key: 'support_date', label: 'Fecha\nSoporte', visible: true, mandatory: true, numeric: false },
          { key: 'amount', label: 'Montante', visible: true, mandatory: false, numeric: true },
          { key: 'in_currency', label: 'Valor\ndivisa\nEURXXX', visible: true, mandatory: false, numeric: true },
          { key: 'tthh_profit', label: 'Rentab.\nLT', visible: true, mandatory: false, numeric: true },
          { key: 'benefit', label: 'Beneficio', visible: true, mandatory: false, numeric: true },
          { key: 'insured_benefit', label: 'Beneficio\nAseg.', visible: true, mandatory: false, numeric: true },
          { key: 'in_date', label: 'F.\nEntrada', visible: true, mandatory: false, numeric: false },
          { key: 'open_fee', label: 'Com.\nApert.', visible: true, mandatory: false, numeric: true },
          { key: 'cfd_margin', label: 'Margen\nCFD', visible: true, mandatory: false, numeric: true },
          { key: 'is_ipo', label: 'IPO', visible: true, mandatory: false, numeric: false },
          { key: 'notes', label: 'Notas', visible: true, mandatory: false, numeric: false, sortable: false },
        ],
      };
    },
     computed: {
        allSelected() {
              return this.openPositions.length > 0 && this.selectedPositions.length === this.openPositions.length;
        },
        formattedCloseDateForInput() {
              return this.formatDateForInput(this.closeFormData.out_date);
        },
        formattedEditSupportDateForInput() {
            if (!this.editFormData.support_date) return '';
            const date = parseDate(this.editFormData.support_date);
            return date ? date.toISOString().split('T')[0] : '';
        },
        groupedAlerts() {
          if (!this.alerts || this.alerts.length === 0) { return {}; }
          // Filter alerts to only show those for open positions (not waiting)
          const openPositionAlerts = this.alerts.filter(alert => alert.instrument._type === 'OpenPosition');
          return openPositionAlerts.reduce((groups, alert) => { const groupName = alert.type_name || 'Alertas Generales'; if (!groups[groupName]) { groups[groupName] = []; } groups[groupName].push(alert); return groups; }, {});
       },
       displayPortfolioCurrency() {
           return this.portfolioCurrency;
        },
        sortedOpenPositions() {
         if (!this.openPositions || this.openPositions.length === 0) {
           return [];
         }
         
         const sorted = [...this.openPositions].sort((a, b) => {
           let aValue = this.getSortValue(a, this.sortBy);
           let bValue = this.getSortValue(b, this.sortBy);
           
           // Handle null/undefined values
           if (aValue === null || aValue === undefined) aValue = '';
           if (bValue === null || bValue === undefined) bValue = '';
           
           // Convert to strings for comparison if needed
           if (typeof aValue === 'string') aValue = aValue.toLowerCase();
           if (typeof bValue === 'string') bValue = bValue.toLowerCase();
           
           let comparison = 0;
           if (aValue < bValue) comparison = -1;
           else if (aValue > bValue) comparison = 1;
           
           return this.sortDirection === 'desc' ? -comparison : comparison;
         });
         
         return sorted;
       },
        isEditFormValid() {
            return this.editInstrumentNumberValid && this.editInPriceValid && 
                   (!this.editManualMode || this.editOutPriceValid) &&
                   this.editSupportPriceValid && 
                   this.editSupportDateValid && 
                   (this.positionToEdit.instrument_type !== 'CFD' || this.editCfdMarginValid) && 
                   !this.hasEditErrors;
        },
        hasEditErrors() {
            return !!(this.editInstrumentNumberError || this.editInPriceError || 
                     (this.editManualMode && this.editOutPriceError) ||
                     this.editSupportPriceError || 
                     this.editSupportDateError ||
                     (this.positionToEdit && this.positionToEdit.instrument_type === 'CFD' && this.editCfdMarginError) ||
                     this.editInCurrencyError ||
                     this.editOpenFeeError);
        },
        followEurekersMethodology: {
            get() {
                return !this.editManualMode;
            },
            set(value) {
                this.editManualMode = !value;
            }
        },
        visibleColumns() {
            return this.activeColumns.filter(column => column.visible);
        },
     },
    methods: {
      // --- Anglo Number Formatting Helpers ---
      formatNumberAnglo(value, decimals = 0) {
        if (value === null || value === undefined || value === '') return 'N/A';
        const num = typeof value === 'number' ? value : Number(value);
        if (isNaN(num)) return 'N/A';
        const absValue = Math.abs(num);
        const parts = absValue.toFixed(decimals).split('.');
        const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const decimalPart = parts[1];
        return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
      },
      formatCurrencyAnglo(value, currency = 'EUR') {
        if (value === null || value === undefined || value === '' || value === 0) return '0.00€';
        const num = typeof value === 'number' ? value : Number(value);
        if (isNaN(num)) return 'N/A';
        const isNegative = num < 0;
        const formattedNumber = this.formatNumberAnglo(Math.abs(num), 2);
        const currencySymbol = currency === 'EUR' ? '€' : currency;
        return isNegative ? `-${formattedNumber}${currencySymbol}` : `${formattedNumber}${currencySymbol}`;
      },
      formatCurrency(value, currency = 'EUR') {
        return this.formatCurrencyAnglo(value, currency);
      },
      // --- Currency Symbol Mapper ---
      getCurrencySymbol(currency) {
        const symbols = {
          'EUR': '€',
          'USD': '$',
          'GBP': '£',
          'JPY': '¥',
          'CNY': '¥',
          'CHF': 'CHF',
          'CAD': 'C$',
          'AUD': 'A$',
          'NZD': 'NZ$',
          'HKD': 'HK$',
          'SGD': 'S$',
          'SEK': 'kr',
          'NOK': 'kr',
          'DKK': 'kr',
          'PLN': 'zł',
          'CZK': 'Kč',
          'HUF': 'Ft',
          'RUB': '₽',
          'INR': '₹',
          'KRW': '₩',
          'TRY': '₺',
          'BRL': 'R$',
          'MXN': 'MX$',
          'CLP': 'CL$',
          'COP': 'CO$',
          'ARS': 'AR$',
          'PEN': 'S/',
          'ZAR': 'R',
          'THB': '฿',
          'MYR': 'RM',
          'IDR': 'Rp',
          'PHP': '₱',
          'VND': '₫',
          'TWD': 'NT$',
          'ILS': '₪',
          'AED': 'د.إ',
          'SAR': '﷼',
          'QAR': 'ر.ق',
          'KWD': 'د.ك',
          'BHD': 'د.ب',
          'OMR': 'ر.ع.',
          'JOD': 'د.أ',
          'LBP': 'ل.ل',
          'EGP': 'ج.م'
        };
        return symbols[currency] || currency;
      },
      formatDate(dateString) { const date = parseDate(dateString); return date ? date.toLocaleDateString('es-ES') : (dateString || ''); },
      formatPercentage(value) { const num = typeof value === 'number' ? value : Number(value); if (isNaN(num)) return 'N/A'; return `${this.formatNumberAnglo(num, 2)}%`; },
      formatPrice(value, decimals) { const num = typeof value === 'number' ? value : Number(value); if (isNaN(num)) return 'N/A'; const numDecimals = (typeof decimals === 'number' && decimals >= 0) ? decimals : 2; return this.formatNumberAnglo(num, numDecimals); },
      formatNumber(value) { const num = typeof value === 'number' ? value : Number(value); return isNaN(num) ? 'N/A' : this.formatNumberAnglo(num, 0); },
      formatDateForInput(dmyDate) { if (!dmyDate) return ''; const date = parseDate(dmyDate); return date ? date.toISOString().split('T')[0] : ''; },
      getTextClass(value) { if (typeof value !== 'number') return ''; return value >= 0 ? 'text-success' : 'text-danger'; },
      isExternalPosition(position) {
        return position && (position.is_external === true || position.is_external === 1 || position.is_external === '1');
      },
      externalAnalysisLabel() {
        return 'NO EUREKERS';
      },
  
      // --- Data Fetching ---
      async fetchData() {
          if (!this.portfolioId) return;
          this.fetchOpenPositions();
          this.fetchAlerts();
      },
      async fetchOpenPositions() {
        this.loading = true;
        this.error = null;
        this.selectedPositions = [];
        try {
          const positions = await apiService.listOpenPositions(this.portfolioId);
          this.openPositions = positions.map(p => ({ ...p, editingNotes: false, editNotesValue: p.notes }));
          if (positions.length > 0 && positions[0].portfolio_currency) {
              this.portfolioCurrency = positions[0].portfolio_currency;
          }
        } catch (err) {
          this.error = `Error cargando posiciones abiertas: ${err.message}`;
          this.openPositions = [];
        } finally {
          this.loading = false;
        }
      },
      async fetchAlerts() {
          this.alertsLoading = true; this.alertsError = null; this.bulkAlertError = null; this.selectedAlerts = [];
          try {
              const response = await apiService.getPortfolioAlerts(this.portfolioId);
              if (response && response.list && Array.isArray(response.list['#items'])) { this.alerts = response.list['#items']; }
              else if (Array.isArray(response)) { this.alerts = response; }
              else { this.alerts = []; console.warn("Unexpected structure for alerts response:", response); }
              this.initializeAlertGroupState();
          } catch (err) { this.alertsError = `Error cargando alertas: ${err.message}`; this.alerts = []; }
           finally { this.alertsLoading = false; }
       },
       initializeAlertGroupState() {
           const newState = {};
           const newBulkActions = {};
           for (const typeName in this.groupedAlerts) {
               newState[typeName] = this.alertGroupCollapsedState[typeName] !== undefined ? this.alertGroupCollapsedState[typeName] : true; // Default collapsed
               newBulkActions[typeName] = this.alertGroupBulkActions[typeName] || ''; // Default empty action
           }
           this.alertGroupCollapsedState = newState;
           this.alertGroupBulkActions = newBulkActions;
       },
  
      // --- Modal & Form Handling (Keep) ---
      openImportModal() { this.formError = null; this.showImportModal = true; },
      closeImportModal() { this.showImportModal = false; },
      setFormError(message) { this.formError = message; },
      openCloseModal(position) {
           this.closeFormError = null;
          this.positionToClose = position;
          this.closeFormData = {
              instrument_id: position.id,
              out_price: position.current_price,
              out_currency: position.current_currency_price || null,
              currency_pair: position.portfolio_currency_pair || '',
              out_date: formatDateDMY(new Date()),
              close_fee: position.close_fee !== undefined ? position.close_fee : 0,
          };
          this.showCloseModal = true;
      },
      closeCloseModal() { this.showCloseModal = false; this.positionToClose = null; },
      openEditModal(position) {
          this.editFormError = null;
          this.positionToEdit = position;
          this.editFormData = {
              instrument_number: position.instrument_number,
              in_price: position.in_price,
              out_price: position.out_price,
              support_price: position.support_price,
              support_date: position.support_date ? formatDateDMY(parseDate(position.support_date)) : '',
              cfd_margin: position.cfd_margin,
              in_currency: position.in_currency,
              open_fee: position.open_fee,
          };
          
          // Reset manual mode
          this.editManualMode = false;
          
          // Reset validation state
          this.editInstrumentNumberError = null;
          this.editInPriceError = null;
          this.editOutPriceError = null;
          this.editSupportPriceError = null;
          this.editSupportDateError = null;
          this.editCfdMarginError = null;
          this.editInCurrencyError = null;
          this.editOpenFeeError = null;
          this.editInstrumentNumberValid = false;
          this.editInPriceValid = false;
          this.editOutPriceValid = false;
          this.editSupportPriceValid = false;
          this.editSupportDateValid = false;
          this.editCfdMarginValid = false;
          this.editInCurrencyValid = true;
          this.editOpenFeeValid = true;
          this.editTouchedFields = {
              instrument_number: false,
              in_price: false,
              out_price: false,
              support_price: false,
              support_date: false,
              cfd_margin: false,
              in_currency: false,
              open_fee: false
          };
          this.editSubmitAttempted = false;
          
          this.showEditModal = true;
          
          // Validate initial values after modal is shown
          this.$nextTick(() => {
              this.validateEditInitialValues();
          });
      },
      closeEditModal() {
          this.showEditModal = false;
          this.positionToEdit = null;
          this.editFormData = { instrument_number: null, in_price: null, out_price: null, support_price: null, support_date: '', cfd_margin: null, in_currency: null, open_fee: null };
          
          // Reset manual mode
          this.editManualMode = false;
          
          // Reset validation state
          this.editInstrumentNumberError = null;
          this.editInPriceError = null;
          this.editOutPriceError = null;
          this.editSupportPriceError = null;
          this.editSupportDateError = null;
          this.editCfdMarginError = null;
          this.editInCurrencyError = null;
          this.editOpenFeeError = null;
          this.editInstrumentNumberValid = false;
          this.editInPriceValid = false;
          this.editOutPriceValid = false;
          this.editSupportPriceValid = false;
          this.editSupportDateValid = false;
          this.editCfdMarginValid = false;
          this.editInCurrencyValid = true;
          this.editOpenFeeValid = true;
          this.editTouchedFields = {
              instrument_number: false,
              in_price: false,
              out_price: false,
              support_price: false,
              support_date: false,
              cfd_margin: false,
              in_currency: false,
              open_fee: false
          };
          this.editSubmitAttempted = false;
      },
      updateCloseDate(event) {
          const dateValue = event.target.value;
          this.closeFormData.out_date = dateValue ? formatDateDMY(new Date(dateValue + 'T00:00:00')) : '';
       },
      updateEditSupportDate(event) {
          const dateValue = event.target.value;
          this.editFormData.support_date = dateValue ? formatDateDMY(new Date(dateValue + 'T00:00:00')) : '';
          this.validateEditSupportDate();
      },
      submitCloseForm() {
           if (this.$refs.closeFormElement && this.$refs.closeFormElement.checkValidity()) {
               this.handleClosePosition();
           } else {
               if (this.$refs.closeFormElement) this.$refs.closeFormElement.reportValidity();
               this.closeFormError = "Por favor, corrija los errores.";
           }
       },
      submitEditForm() {
          this.editSubmitAttempted = true;
          
          // Mark all fields as touched
          this.markEditFieldTouched('instrument_number');
          this.markEditFieldTouched('in_price');
          if (this.editManualMode) {
              this.markEditFieldTouched('out_price');
          }
          this.markEditFieldTouched('support_price');
          this.markEditFieldTouched('support_date');
          if (this.positionToEdit.instrument_type === 'CFD') {
              this.markEditFieldTouched('cfd_margin');
          }
          this.markEditFieldTouched('in_currency');
          this.markEditFieldTouched('open_fee');

          // Validate all fields
          this.validateEditInstrumentNumber();
          this.validateEditInPrice();
          this.validateEditOutPrice();
          this.validateEditSupportPrice();
          this.validateEditSupportDate();
          this.validateEditCfdMargin();
          this.validateEditInCurrency();
          this.validateEditOpenFee();

          if (!this.isEditFormValid || this.hasEditErrors) {
              this.editFormError = "Por favor, corrija los errores del formulario antes de continuar.";
              return;
          }

          this.handleUpdateOpenPosition();
      },
  
      // --- API Actions (Keep) ---
      async handleImportPosition(formData) {
          this.formError = null; this.isSubmitting = true;
          try { await apiService.importOpenPosition(this.portfolioId, formData); this.closeImportModal(); await this.fetchData(); }
          catch (err) { this.formError = `Error al importar: ${err.message}`; } finally { this.isSubmitting = false; }
      },
      async handleClosePosition() {
          this.closeFormError = null; this.isSubmitting = true;
          const dataToSend = { ...this.closeFormData };
           if (dataToSend.out_currency === null || dataToSend.out_currency === '') { delete dataToSend.out_currency; delete dataToSend.currency_pair; }
          try { await apiService.closeOpenPosition(this.portfolioId, dataToSend); this.closeCloseModal(); await this.fetchData(); }
          catch (err) { this.closeFormError = `Error al cerrar: ${err.message}`; } finally { this.isSubmitting = false; }
      },
      async handleUpdateOpenPosition() {
        if (!this.positionToEdit) return;
        this.editFormError = null;
        this.isSubmitting = true;
        try {
            let updateData;
            if (this.editManualMode) {
                updateData = {
                    instrument_number: this.editFormData.instrument_number,
                    in_price: this.editFormData.in_price,
                    out_price: this.editFormData.out_price,
                    support_price: this.editFormData.support_price,
                    support_date: this.editFormData.support_date,
                    manual_override: 1
                };
            } else {
                updateData = {
                    instrument_number: this.editFormData.instrument_number,
                    in_price: this.editFormData.in_price,
                    support_price: this.editFormData.support_price,
                    support_date: this.editFormData.support_date,
                };
            }
            
            // Add CFD margin only for CFD instruments
            if (this.positionToEdit.instrument_type === 'CFD') {
                updateData.cfd_margin = this.editFormData.cfd_margin;
            }
            // Include optional fields if provided
            if (this.editFormData.in_currency !== null && this.editFormData.in_currency !== '') {
                updateData.in_currency = this.editFormData.in_currency;
            }
            if (this.editFormData.open_fee !== null && this.editFormData.open_fee !== '') {
                updateData.open_fee = this.editFormData.open_fee;
            }
            await apiService.updateOpenPosition(this.portfolioId, this.positionToEdit.id, updateData);
            this.closeEditModal();
            await this.fetchData();
        } catch (err) {
            this.editFormError = `Error al actualizar: ${err.message}`;
        } finally {
            this.isSubmitting = false;
        }
      },

      // --- Edit Form Validation Methods ---
      markEditFieldTouched(fieldName) {
          this.editTouchedFields[fieldName] = true;
      },

      shouldShowEditError(fieldName) {
          return (this.editTouchedFields[fieldName] || this.editSubmitAttempted) && this[`edit${fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace('_', '')}Error`];
      },

      validateEditInstrumentNumber() {
          const instrumentNumber = parseFloat(this.editFormData.instrument_number);
          
          if (this.editFormData.instrument_number === null || this.editFormData.instrument_number === undefined || 
              this.editFormData.instrument_number === '' || isNaN(instrumentNumber)) {
              this.editInstrumentNumberError = 'El número de acciones es requerido y debe ser un número válido.';
              this.editInstrumentNumberValid = false;
          } else if (instrumentNumber <= 0) {
              this.editInstrumentNumberError = 'El número de acciones debe ser mayor que 0.';
              this.editInstrumentNumberValid = false;
          } else if (instrumentNumber > 1000000000) {
              this.editInstrumentNumberError = 'El número de acciones no puede exceder 1.000.000.000.';
              this.editInstrumentNumberValid = false;
          } else {
              this.editInstrumentNumberError = null;
              this.editInstrumentNumberValid = true;
          }
      },

      validateEditInPrice() {
          const inPrice = parseFloat(this.editFormData.in_price);
          
          if (this.editFormData.in_price === null || this.editFormData.in_price === undefined || 
              this.editFormData.in_price === '' || isNaN(inPrice)) {
              this.editInPriceError = 'El precio de entrada es requerido y debe ser un número válido.';
              this.editInPriceValid = false;
          } else if (inPrice <= 0) {
              this.editInPriceError = 'El precio de entrada debe ser mayor que 0.';
              this.editInPriceValid = false;
          } else if (inPrice > 1000000) {
              this.editInPriceError = 'El precio de entrada no puede exceder 1.000.000.';
              this.editInPriceValid = false;
          } else {
              this.editInPriceError = null;
              this.editInPriceValid = true;
          }
      },

      validateEditOutPrice() {
          if (!this.editManualMode) {
              // In automatic mode, out_price validation is not required
              this.editOutPriceError = null;
              this.editOutPriceValid = true;
              return;
          }
          
          const outPrice = parseFloat(this.editFormData.out_price);
          
          if (this.editFormData.out_price === null || this.editFormData.out_price === undefined || 
              this.editFormData.out_price === '' || isNaN(outPrice)) {
              this.editOutPriceError = 'El precio de salida es requerido en modo manual y debe ser un número válido.';
              this.editOutPriceValid = false;
          } else if (outPrice <= 0) {
              this.editOutPriceError = 'El precio de salida debe ser mayor que 0.';
              this.editOutPriceValid = false;
          } else if (outPrice > 1000000) {
              this.editOutPriceError = 'El precio de salida no puede exceder 1.000.000.';
              this.editOutPriceValid = false;
          } else {
              this.editOutPriceError = null;
              this.editOutPriceValid = true;
          }
      },

      validateEditSupportPrice() {
          const supportPrice = parseFloat(this.editFormData.support_price);
          
          if (this.editFormData.support_price === null || this.editFormData.support_price === undefined || 
              this.editFormData.support_price === '' || isNaN(supportPrice)) {
              this.editSupportPriceError = 'El precio de soporte es requerido y debe ser un número válido.';
              this.editSupportPriceValid = false;
          } else if (supportPrice <= 0) {
              this.editSupportPriceError = 'El precio de soporte debe ser mayor que 0.';
              this.editSupportPriceValid = false;
          } else if (supportPrice > 1000000) {
              this.editSupportPriceError = 'El precio de soporte no puede exceder 1.000.000.';
              this.editSupportPriceValid = false;
          } else {
              this.editSupportPriceError = null;
              this.editSupportPriceValid = true;
          }
      },

      validateEditSupportDate() {
          if (!this.editFormData.support_date) {
              this.editSupportDateError = 'La fecha de soporte es requerida.';
              this.editSupportDateValid = false;
              return;
          }

          const parsedDate = parseDate(this.editFormData.support_date);
          if (!parsedDate) {
              this.editSupportDateError = 'La fecha ingresada no es válida.';
              this.editSupportDateValid = false;
              return;
          }

          // Check if date is not in the future
          const today = new Date();
          today.setHours(23, 59, 59, 999);
          if (parsedDate > today) {
              this.editSupportDateError = 'La fecha de soporte no puede ser futura.';
              this.editSupportDateValid = false;
              return;
          }

          this.editSupportDateError = null;
          this.editSupportDateValid = true;
      },

             validateEditCfdMargin() {
           // Only validate CFD margin for CFD instruments
           if (!this.positionToEdit || this.positionToEdit.instrument_type !== 'CFD') {
               this.editCfdMarginError = null;
               this.editCfdMarginValid = true;
               return;
           }

           const cfdMargin = parseFloat(this.editFormData.cfd_margin);
           
           if (this.editFormData.cfd_margin === null || this.editFormData.cfd_margin === undefined || 
               this.editFormData.cfd_margin === '' || isNaN(cfdMargin)) {
               this.editCfdMarginError = 'El margen CFD es requerido y debe ser un número válido.';
               this.editCfdMarginValid = false;
           } else if (cfdMargin <= 0) {
               this.editCfdMarginError = 'El margen CFD debe ser mayor que 0.';
               this.editCfdMarginValid = false;
           } else if (cfdMargin > 100) {
               this.editCfdMarginError = 'El margen CFD no puede exceder 100.';
               this.editCfdMarginValid = false;
           } else {
               this.editCfdMarginError = null;
               this.editCfdMarginValid = true;
           }
       },

      validateEditInCurrency() {
          const val = this.editFormData.in_currency;
          if (val === null || val === undefined || val === '') {
              this.editInCurrencyError = null;
              this.editInCurrencyValid = true;
              return;
          }
          const num = parseFloat(val);
          if (isNaN(num) || !isFinite(num)) {
              this.editInCurrencyError = 'El valor de divisa debe ser un número válido.';
              this.editInCurrencyValid = false;
          } else if (num <= 0) {
              this.editInCurrencyError = 'El valor de divisa debe ser mayor que 0.';
              this.editInCurrencyValid = false;
          } else if (num > 1000000) {
              this.editInCurrencyError = 'El valor de divisa no puede exceder 1.000.000.';
              this.editInCurrencyValid = false;
          } else {
              this.editInCurrencyError = null;
              this.editInCurrencyValid = true;
          }
      },

      validateEditOpenFee() {
          const val = this.editFormData.open_fee;
          if (val === null || val === undefined || val === '') {
              this.editOpenFeeError = null;
              this.editOpenFeeValid = true;
              return;
          }
          const num = parseFloat(val);
          if (isNaN(num) || !isFinite(num)) {
              this.editOpenFeeError = 'La comisión de apertura debe ser un número válido.';
              this.editOpenFeeValid = false;
          } else if (num < 0) {
              this.editOpenFeeError = 'La comisión de apertura no puede ser negativa.';
              this.editOpenFeeValid = false;
          } else if (num > 1000000) {
              this.editOpenFeeError = 'La comisión de apertura no puede exceder 1.000.000.';
              this.editOpenFeeValid = false;
          } else {
              this.editOpenFeeError = null;
              this.editOpenFeeValid = true;
          }
      },

      formatEditDisplayValue(value) {
          if (value === null || value === undefined) return '';
          if (typeof value === 'number') {
              return value.toString();
          }
          return value;
      },

      handleEditFocus(event, modelKey) {
          if (this.editFormData[modelKey] !== null && this.editFormData[modelKey] !== undefined) {
              // Display raw value for editing
              event.target.value = this.editFormData[modelKey].toString();
          }
      },

      handleEditNumericInput(event, modelKey) {
          let value = event.target.value;
          // Only allow digits, dots, and commas (no dashes/minus signs)
          let sanitizedValue = value.replace(/[^\d.,]/g, '');
          
          // Handle decimal separators: find the last comma or dot as decimal separator
          const lastCommaIndex = sanitizedValue.lastIndexOf(',');
          const lastDotIndex = sanitizedValue.lastIndexOf('.');
          const lastDecimalIndex = Math.max(lastCommaIndex, lastDotIndex);
          
          if (lastDecimalIndex !== -1) {
              // Split into integer and decimal parts
              const integerPart = sanitizedValue.substring(0, lastDecimalIndex).replace(/[.,]/g, ''); // Remove all dots/commas from integer part
              const decimalPart = sanitizedValue.substring(lastDecimalIndex + 1);
              
              // Limit decimal part to appropriate number of digits
              let maxDecimals;
              if (modelKey === 'in_currency') {
                  maxDecimals = 6; // Allow 6 decimal places for currency values
              } else if (modelKey.includes('price') && this.positionToEdit) {
                  maxDecimals = this.positionToEdit.stock_decimals || 2;
              } else {
                  maxDecimals = 2;
              }
              const limitedDecimalPart = decimalPart.substring(0, maxDecimals);
              
              // Reconstruct with dot as decimal separator
              sanitizedValue = integerPart + '.' + limitedDecimalPart;
          }
          
          // Prevent starting with decimal point - require leading zero
          if (sanitizedValue.startsWith('.')) {
              sanitizedValue = '0' + sanitizedValue;
          }

          event.target.value = sanitizedValue;

          if (sanitizedValue === '') {
              this.editFormData[modelKey] = null;
          } else if (!isNaN(parseFloat(sanitizedValue)) && isFinite(sanitizedValue)) {
              this.editFormData[modelKey] = sanitizedValue;
          } else if (sanitizedValue.endsWith('.')) {
              this.editFormData[modelKey] = sanitizedValue;
          } else {
              this.editFormData[modelKey] = null;
          }

          // Real-time validation
          this.validateEditField(modelKey);
      },

      formatEditNumericOnBlur(event, modelKey) {
          let value = this.editFormData[modelKey];
          
          if (value === null || String(value).trim() === '') {
              this.editFormData[modelKey] = null;
              return;
          }
          
          if(typeof value === 'string' && value.endsWith('.')) {
              value = value.slice(0, -1);
          }

          const numericValue = parseFloat(value);
          if (!isNaN(numericValue) && isFinite(numericValue)) {
              // Round to appropriate decimal places
              let maxDecimals;
              if (modelKey === 'in_currency') {
                  maxDecimals = 6; // Allow 6 decimal places for currency values
              } else if (modelKey.includes('price') && this.positionToEdit) {
                  maxDecimals = this.positionToEdit.stock_decimals || 2;
              } else {
                  maxDecimals = 2;
              }
              const roundedValue = Math.round(numericValue * Math.pow(10, maxDecimals)) / Math.pow(10, maxDecimals);
              this.editFormData[modelKey] = roundedValue;
          } else {
              this.editFormData[modelKey] = null;
          }

          // Validate after formatting
          this.validateEditField(modelKey);
      },

      validateEditField(modelKey) {
          switch(modelKey) {
              case 'instrument_number':
                  this.validateEditInstrumentNumber();
                  break;
              case 'in_price':
                  this.validateEditInPrice();
                  break;
              case 'out_price':
                  this.validateEditOutPrice();
                  break;
              case 'support_price':
                  this.validateEditSupportPrice();
                  break;
              case 'support_date':
                  this.validateEditSupportDate();
                  break;
              case 'cfd_margin':
                  this.validateEditCfdMargin();
                  break;
              case 'in_currency':
                  this.validateEditInCurrency();
                  break;
              case 'open_fee':
                  this.validateEditOpenFee();
                  break;
          }
      },

      validateEditInitialValues() {
          if (this.editFormData.instrument_number !== null) this.validateEditInstrumentNumber();
          if (this.editFormData.in_price !== null) this.validateEditInPrice();
          if (this.editFormData.out_price !== null) this.validateEditOutPrice();
          if (this.editFormData.support_price !== null) this.validateEditSupportPrice();
          if (this.editFormData.support_date) this.validateEditSupportDate();
          if (this.editFormData.cfd_margin !== null) this.validateEditCfdMargin();
          if (this.editFormData.in_currency !== null) this.validateEditInCurrency();
          if (this.editFormData.open_fee !== null) this.validateEditOpenFee();
      },
      async confirmDelete(positionId) {
          if (confirm('¿Está seguro? Esto eliminará permanentemente la posición abierta SIN moverla al histórico. Normalmente, debería usar la opción "Cerrar Posición".')) {
              this.isSubmitting = true; this.error = null;
              try { await apiService.deleteOpenPosition(this.portfolioId, positionId); await this.fetchData(); }
              catch (err) { this.error = `Error al eliminar: ${err.message}`; } finally { this.isSubmitting = false; }
          }
      },
      startEditNotes(position) {
           this.openPositions.forEach(p => p.editingNotes = false);
           position.editNotesValue = position.notes; position.editingNotes = true;
           this.$nextTick(() => {
               const textareas = this.$refs.notesTextarea;
               if (textareas) { const targetTextarea = textareas.find(ta => ta.dataset.id === position.id); if (targetTextarea) targetTextarea.focus(); }
           });
       },
      cancelEditNotes(position) { position.editingNotes = false; },
      async saveNotes(position) {
           if (position.notes === position.editNotesValue) { position.editingNotes = false; return; }
           this.isSubmitting = true;
           try {
               const updateData = { notes: position.editNotesValue };
               const updatedPosition = await apiService.updateOpenPosition(this.portfolioId, position.id, updateData);
                const index = this.openPositions.findIndex(p => p.id === position.id);
                if (index !== -1) { this.$set(this.openPositions[index], 'notes', updatedPosition.notes); this.$set(this.openPositions[index], 'editingNotes', false); }
                else { await this.fetchData(); }
           } catch (err) { console.error("Error saving notes:", err); alert(`Error guardando notas: ${err.message}`); }
           finally { this.isSubmitting = false; }
       },

      // --- Exchange Rate Fetching ---
      async fetchExchangeRateManually() {
          if (!this.positionToClose || !this.positionToClose.currency || !this.portfolioCurrency) {
              this.closeFormError = 'No se puede obtener tasa de cambio: faltan datos de divisa';
              return;
          }

          // Si las divisas son iguales, el tipo de cambio es 1
          if (this.positionToClose.currency === this.portfolioCurrency) {
              this.closeFormData.out_currency = 1;
              this.closeFormData.currency_pair = `${this.positionToClose.currency}/${this.portfolioCurrency}`;
              return;
          }

          this.exchangeRateLoading = true;
          this.closeFormError = null; // Clear any previous errors
          
          try {
              const rate = await this.getExchangeRate(this.positionToClose.currency, this.portfolioCurrency);
              this.closeFormData.out_currency = rate;
              this.closeFormData.currency_pair = `${this.positionToClose.currency}/${this.portfolioCurrency}`;
          } catch (error) {
              console.error('Error obteniendo tasa de cambio:', error);
              this.closeFormError = `Error obteniendo tasa de cambio ${this.positionToClose.currency}/${this.portfolioCurrency}: ${error.message}`;
          } finally {
              this.exchangeRateLoading = false;
          }
      },

      // --- Método para obtener tasa de cambio via API ---
      getExchangeRate(fromCurrency, toCurrency) {
          return new Promise((resolve, reject) => {
              if (fromCurrency === toCurrency) {
                  resolve(1);
                  return;
              }
              
              const url = `/api/v1/calculator-exchanges/${fromCurrency}/${toCurrency}`;
              
              const successCallback = response => {
                  const rate = response?.data?.chart?.result?.[0]?.meta?.chartPreviousClose;
                  if (typeof rate === 'number') {
                      const formattedRate = parseFloat(rate.toFixed(4));
                      resolve(formattedRate);
                  } else {
                      reject(new Error(`Respuesta inesperada para ${fromCurrency}/${toCurrency}`));
                  }
              };
              
              const failureCallback = (error) => {
                  reject(new Error(`No se pudo obtener tasa ${fromCurrency}/${toCurrency}`));
              };

              const successHandler = new APICaller.SuccessHandler(successCallback);
              const failureHandler = new APICaller.FailureHandler('API_ERR_EXCHANGE_RATE', `Error API ${fromCurrency}/${toCurrency}`, failureCallback);
              this._getAPICall(url, successHandler, failureHandler);
          });
      },
  
      // --- Bulk Actions (Keep) ---
       toggleSelectAll(event) {
           this.selectedPositions = event.target.checked ? this.openPositions.map(p => p.id) : [];
       },
      async performBulkDeleteOpenPositions() {
           this.error = null;
           let failedActions = 0;
           try {
               for (const positionId of this.selectedPositions) {
                   try {
                       await apiService.deleteOpenPosition(this.portfolioId, positionId);
                   } catch (err) {
                       console.error(`Failed bulk delete ${positionId}:`, err);
                       failedActions++;
                   }
               }
           } catch (err) {
               throw new Error(`Error durante acción múltiple: ${err.message}`);
           }
           await Promise.all([this.fetchOpenPositions(), this.fetchAlerts()]);
           if (failedActions > 0) {
               throw new Error(`${failedActions} eliminaciones fallaron.`);
           }
       },
      applyBulkAction() {
           if (this.selectedPositions.length === 0) return;
           this.openConfirmModal(
               'Confirmar Eliminación Múltiple',
               `¿ELIMINAR ${this.selectedPositions.length} posiciones abiertas seleccionadas? Esta acción es permanente y no va al histórico.`,
               this.performBulkDeleteOpenPositions,
               null
           );
       },
  
       // --- Alert Group Collapse ---
       toggleAlertGroup(typeName) { this.$set(this.alertGroupCollapsedState, typeName, !this.alertGroupCollapsedState[typeName]); },

       // --- Alert Action Methods ---
       async reportAlertError(alert) {
           this.openReportErrorModal(alert);
       },
        async performApplyAlertSolution(alert) {
            // Actual logic to apply solution
            this.alertsLoading = true; // Use alerts section loading indicator
            try {
                await apiService.applyAlertSolution(this.portfolioId, alert.id);
                await this.fetchData(); // Refreshes alerts and potentially positions
            } catch (err) {
                throw new Error(`Al aplicar solución para alerta en ${alert.ticker}: ${err.message}`);
            } finally {
                this.alertsLoading = false;
            }
        },
        applyAlertSolution(alert) {
           this.openConfirmModal(
               'Aplicar Solución de Alerta',
               `¿Confirmas que quieres aplicar la solución automática para la alerta en ${alert.ticker} (${alert.data.message})?`,
               () => this.performApplyAlertSolution(alert),
               alert // Pass alert data if needed, though action uses alert from its closure
           );
        },
        async ignoreAlert(alert) {
           this.alertsLoading = true;
            try { await apiService.createAlertState(this.portfolioId, { alert_id: alert.id, state: 'ignore', ticker: alert.ticker }); await this.fetchAlerts(); }
            catch (err) { this.alertsError = `Error ignorando alerta: ${err.message}`; } finally { this.alertsLoading = false; }
        },

       // --- Bulk Actions (Alerts) ---
       areAllAlertsInGroupSelected(groupAlerts) { if (!groupAlerts || groupAlerts.length === 0) return false; return groupAlerts.every(alert => this.selectedAlerts.includes(alert.id)); },
       toggleSelectAllAlertsInGroup(event, groupAlerts) {
           const groupAlertIds = groupAlerts.map(alert => alert.id);
           if (event.target.checked) { groupAlertIds.forEach(id => { if (!this.selectedAlerts.includes(id)) { this.selectedAlerts.push(id); } }); }
           else { this.selectedAlerts = this.selectedAlerts.filter(id => !groupAlertIds.includes(id)); }
       },
              async performBulkAlertActionForGroup(selectedAlertIds, action, typeName) {
             // Actual logic for bulk alert actions for a specific group
             this.alertsLoading = true;
             this.bulkAlertError = null; // Use this for specific feedback below the alerts table
             let successes = 0;
             let failures = 0;

             for (const alertId of selectedAlertIds) {
                 const alert = this.alerts.find(a => a.id === alertId);
                 if (!alert) {
                     failures++;
                     continue;
                 }
                 try {
                     if (action === 'ignore') {
                         await apiService.createAlertState(this.portfolioId, { alert_id: alert.id, state: 'ignore', ticker: alert.ticker });
                     } else if (action === 'apply_solution') {
                         if (alert.can_it_be_solved_automatically) {
                             await apiService.applyAlertSolution(this.portfolioId, alert.id);
                         } else {
                             console.warn(`Solución automática no disponible para alerta ${alertId} (${alert.ticker})`);
                             failures++; // Count as failure if solution cannot be applied
                             continue;
                         }
                     }
                     successes++;
                 } catch (err) {
                     console.error(`Error durante acción múltiple '${action}' en alerta ${alertId} (${alert.ticker}):`, err);
                     failures++;
                 }
             }
             await this.fetchData(); // Refresh alerts and potentially positions

             if (failures > 0) {
                 this.bulkAlertError = `Acción '${action}' completada para ${successes} alertas. ${failures} fallaron (ver consola para detalles).`;
                 // Do not throw error here to keep it displayed as bulkAlertError, not in modal
             } else {
                 // this.$toast.success(`Acción '${action}' completada para ${successes} alertas.`); // Optional toast for success
                 console.log(`Acción '${action}' completada para ${successes} alertas.`);
             }
             this.alertsLoading = false;
             // Remove processed alerts from global selection
             this.selectedAlerts = this.selectedAlerts.filter(id => !selectedAlertIds.includes(id));
             // Reset only this group's dropdown
             this.$set(this.alertGroupBulkActions, typeName, '');
        },
       async performBulkAlertAction() {
            // Actual logic for bulk alert actions
            this.alertsLoading = true;
            this.bulkAlertError = null; // Use this for specific feedback below the alerts table
            let successes = 0;
            let failures = 0;
            const action = this.selectedBulkAlertAction; // Get action from data property
            const alertIds = [...this.selectedAlerts]; // Use current selection

            for (const alertId of alertIds) {
                const alert = this.alerts.find(a => a.id === alertId);
                if (!alert) {
                    failures++;
                    continue;
                }
                try {
                    if (action === 'ignore') {
                        await apiService.createAlertState(this.portfolioId, { alert_id: alert.id, state: 'ignore', ticker: alert.ticker });
                    } else if (action === 'apply_solution') {
                        if (alert.can_it_be_solved_automatically) {
                            await apiService.applyAlertSolution(this.portfolioId, alert.id);
                        } else {
                            console.warn(`Solución automática no disponible para alerta ${alertId} (${alert.ticker})`);
                            failures++; // Count as failure if solution cannot be applied
                            continue;
                        }
                    }
                    successes++;
                } catch (err) {
                    console.error(`Error durante acción múltiple '${action}' en alerta ${alertId} (${alert.ticker}):`, err);
                    failures++;
                }
            }
            await this.fetchData(); // Refresh alerts and potentially positions

            if (failures > 0) {
                this.bulkAlertError = `Acción '${action}' completada para ${successes} alertas. ${failures} fallaron (ver consola para detalles).`;
                // Do not throw error here to keep it displayed as bulkAlertError, not in modal
            } else {
                // this.$toast.success(`Acción '${action}' completada para ${successes} alertas.`); // Optional toast for success
                console.log(`Acción '${action}' completada para ${successes} alertas.`);
            }
            this.alertsLoading = false;
            this.selectedAlerts = []; // Clear selection after action
            this.selectedBulkAlertAction = ''; // Reset dropdown
       },
       getSelectedAlertsInGroup(groupAlerts) {
           const groupAlertIds = groupAlerts.map(alert => alert.id);
           return this.selectedAlerts.filter(alertId => groupAlertIds.includes(alertId));
       },
       applyBulkAlertActionForGroup(groupAlerts, typeName) {
           const selectedInGroup = this.getSelectedAlertsInGroup(groupAlerts);
           const selectedAction = this.alertGroupBulkActions[typeName];
           if (selectedInGroup.length === 0 || !selectedAction) return;
           
           const action = selectedAction;
           const numSelected = selectedInGroup.length;
           const groupName = groupAlerts.length > 0 ? groupAlerts[0].type_name || 'Alertas Generales' : 'Alertas';
           let confirmationMessage = `¿Estás seguro de que quieres aplicar la acción "${selectedAction === 'ignore' ? 'Ignorar' : 'Aplicar Solución'}" a las ${numSelected} alertas seleccionadas en el grupo "${groupName}"?`;
           if (action === 'apply_solution') {
               confirmationMessage += "\nSe intentará aplicar la solución automática donde sea posible.";
           }

           this.openConfirmModal(
               'Confirmar Acción Múltiple de Alertas',
               confirmationMessage,
               () => this.performBulkAlertActionForGroup(selectedInGroup, selectedAction, typeName), // Action to call
               null // No specific data needed, uses selectedInGroup
           );
       },
       applyBulkAlertAction() {
           if (this.selectedAlerts.length === 0 || !this.selectedBulkAlertAction) return;
           
           const action = this.selectedBulkAlertAction;
           const numSelected = this.selectedAlerts.length;
           let confirmationMessage = `¿Estás seguro de que quieres aplicar la acción "${this.selectedBulkAlertAction === 'ignore' ? 'Ignorar' : 'Aplicar Solución'}" a las ${numSelected} alertas seleccionadas?`;
           if (action === 'apply_solution') {
               confirmationMessage += "\nSe intentará aplicar la solución automática donde sea posible.";
           }

           this.openConfirmModal(
               'Confirmar Acción Múltiple de Alertas',
               confirmationMessage,
               this.performBulkAlertAction, // Action to call
               null // No specific data needed, uses this.selectedAlerts & this.selectedBulkAlertAction
           );
       },
  
      // --- Helpers (Keep) ---
      buildChartUrl(params) {
          // Assuming params is an object like {s: 'TICKER', d: '1'}
          // Or if it's just the ticker string:
          if (typeof params === 'string') return `/apps/chart?s=${params}&d=1`;
          const baseUrl = '/apps/chart'; // Or your actual chart app path
          const query = new URLSearchParams(params).toString();
          return `${baseUrl}?${query}`;
      },
      generateTickerTooltip(pos) {
        const instrument = `Instrumento: ${pos.instrument_type === 'STOCK' ? 'Acción' : 'CFD'}`;
        const currency = `Moneda: ${pos.currency || 'N/A'}`;
        return `${instrument}\n${currency}`;
      },
      priceStep(decimals) {
        return (typeof decimals === 'number' && decimals >= 0) ? Math.pow(10, -decimals) : 0.01;
      },
      getAlertTypeClass(key){ console.log('Alert type key:', key); const m={'not_eureker':'alert-type-not-eureker','support':'alert-type-support-changed','risk':'alert-type-risk-changed','entry_price_changed':'alert-type-entry-price-changed','possible_entry':'alert-type-possible-execution','stop_is_broken':'alert-type-stop-broken','max_amount_in_one_position':'alert-type-max-amount','stop':'alert-type-stop-broken','max_amount':'alert-type-max-amount','possible_execution':'alert-type-possible-execution'}; return m[key]||'alert-type-default'; },
      openDynamicChart(ticker) {
          const url = this.$detectEnvironment() + "/app/graficador-dinamico/ticker/" + ticker;
          window.open(url, "_blank");
      },
      openStockChart(position) {
          // Convert in_date to Unix timestamp for teu parameter
          let teu = '';
          if (position.in_date) {
              const entryDate = parseDate(position.in_date);
              if (entryDate) {
                  teu = Math.floor(entryDate.getTime() / 1000);
              }
          }
          
          const url = `/stockchart?s=${position.ticker}&t=m&sp=${position.support_price}&sl=${position.out_price}&d=1&peu=${position.in_price}${teu ? `&teu=${teu}` : ''}`;
          window.open(url, "graficador", "width=1280, height=720, scrollbars=no, menubar=no, resizable=no");
      },

      // --- Generic Confirmation Modal Methods ---
      openConfirmModal(title, message, action, data) {
        this.formError = null; // Clear general formError when opening confirmation
        this.closeFormError = null; // Clear specific modal errors too
        this.editFormError = null;
        this.bulkAlertError = null;
        this.confirmModalTitle = title;
        this.confirmModalMessage = message;
        this.confirmModalAction = action; // This will be the function to call
        this.confirmModalData = data;     // Data to pass to the action function
        this.showConfirmModal = true;
      },
      closeConfirmModal() {
        this.showConfirmModal = false;
        if (this.isConfirmSubmitting) { // Only reset if it was actually submitting
            this.isConfirmSubmitting = false;
        }
      },
      async executeConfirmAction() {
        if (!this.confirmModalAction) return;
        
        // Use a local error var for the confirmation modal action itself, to distinguish from parent formError prop
        let confirmationActionError = null; 
        this.isConfirmSubmitting = true;
        try {
          await this.confirmModalAction(this.confirmModalData); // Call the stored action
          this.closeConfirmModal(); // Close confirm modal on success of the action
        } catch (err) {
          confirmationActionError = `Error en la acción: ${err.message || 'Ocurrió un error.'}`;
          console.error("Confirmation Action failed:", err);
          // Display this specific error. We could use this.formError if we ensure it's cleared correctly before other modals use it.
          this.formError = confirmationActionError; // Use the general formError for display in this modal
          // Keep the modal open to show the error
        } finally {
          this.isConfirmSubmitting = false;
        }
      },

      // --- Report Alert Error Modal Methods ---
      openReportErrorModal(alert) {
          this.alertToReportError = alert;
          this.reportErrorMessage = '';
          this.reportErrorFormError = null;
          this.showReportErrorModal = true;
      },
      closeReportErrorModal() {
          this.showReportErrorModal = false;
          this.alertToReportError = null;
          this.reportErrorMessage = '';
          this.reportErrorSubmitting = false;
      },
      async handleReportErrorConfirmed() {
          if (!this.alertToReportError) return;
          this.reportErrorSubmitting = true;
          this.reportErrorFormError = null;
          try {
              await apiService.createAlertState(this.portfolioId, {
                  alert_id: this.alertToReportError.id,
                  state: 'error_reported',
                  ticker: this.alertToReportError.ticker,
                  message: this.reportErrorMessage || 'Error reportado sin mensaje adicional.' // Use default if empty
              });
              await this.fetchAlerts();
              this.closeReportErrorModal();
          } catch (err) {
              this.reportErrorFormError = `Error reportando: ${err.message}`;
          } finally {
              this.reportErrorSubmitting = false;
          }
      },

      // --- Sorting Methods ---
      getSortValue(position, sortKey) {
        switch (sortKey) {
          case 'ticker':
            return position.ticker || '';
          case 'risk':
            return position.risk || 0;
          case 'instrument_number':
            return position.instrument_number || 0;
          case 'in_price':
            return position.in_price || 0;
          case 'out_price':
            return position.out_price || 0;
          case 'current_price':
            return position.current_price || 0;
          case 'support_price':
            return position.support_price || 0;
          case 'support_date':
            return position.support_date ? parseDate(position.support_date) : new Date(0);
          case 'in_currency':
            return position.in_currency || 0;
          case 'amount':
            return position.amount || 0;
          case 'benefit':
            return position.benefit || 0;
          case 'insured_benefit':
            return position.insured_benefit || 0;
          case 'tthh_profit':
            return position.tthh_profit || 0;
          case 'cfd_margin':
            return position.cfd_margin || 0;
          case 'is_ipo':
            return position.is_ipo || 0;
          case 'in_date':
            return position.in_date ? parseDate(position.in_date) : new Date(0);
          case 'open_fee':
            return position.open_fee || 0;
          case 'notes':
            return position.notes || '';
          case 'company_name':
            return position.company_name || '';
          case 'direction':
            return position.direction || 0;
          case 'instrument_type':
            return position.instrument_type || '';
          case 'currency':
            return position.currency || '';
          default:
            return position[sortKey] || '';
        }
      },

      sortByColumn(column) {
        if (this.sortBy === column) {
          // If clicking the same column, toggle direction
          this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
          // If clicking a different column, set it as the sort column with ascending direction
          this.sortBy = column;
          this.sortDirection = 'asc';
        }
      },

      getSortIconClass(column) {
        if (this.sortBy !== column) {
          return 'sort-icon sort-none';
        }
        return this.sortDirection === 'asc' ? 'sort-icon sort-asc' : 'sort-icon sort-desc';
      },

      // --- Profitability Calculation Methods ---
      calculateCurrentProfitability(position) {
        if (!position.current_price || !position.in_price || position.in_price <= 0) {
          return 'N/A';
        }
        
        const profitability = ((position.current_price - position.in_price) / position.in_price) * 100;
        const sign = profitability >= 0 ? '+' : '-';
        return `${sign}${this.formatNumberAnglo(profitability, 2)}%`;
      },

      getProfitabilityClass(position) {
        if (!position.current_price || !position.in_price || position.in_price <= 0) {
          return '';
        }
        
        const profitability = ((position.current_price - position.in_price) / position.in_price) * 100;
        return profitability >= 0 ? 'text-success' : 'text-danger';
      },

      // --- Column Customization Methods ---
      handleColumnsChanged(newColumns) {
          this.activeColumns = newColumns;
      },

      initializeColumns() {
          // Try to load saved configuration first
          const savedConfig = this.loadColumnConfiguration();
          
          if (savedConfig && savedConfig.length > 0) {
              this.activeColumns = this.mergeWithSavedConfig(savedConfig);
          } else {
              // Initialize active columns from defaultColumns if no saved config
              this.activeColumns = JSON.parse(JSON.stringify(this.defaultColumns));
          }
      },

      loadColumnConfiguration() {
          try {
              const saved = localStorage.getItem('tableColumns_posicionesAbiertas');
              return saved ? JSON.parse(saved) : null;
          } catch (error) {
              console.warn('Error loading column configuration:', error);
              return null;
          }
      },

      mergeWithSavedConfig(savedConfig) {
          const merged = [];
          
          // First, add columns in the order they were saved
          savedConfig.forEach(savedCol => {
              const currentCol = this.defaultColumns.find(col => col.key === savedCol.key);
              if (currentCol) {
                  merged.push({
                      ...currentCol,
                      visible: savedCol.visible !== undefined ? savedCol.visible : currentCol.visible
                  });
              }
          });
          
          // Then, add any new columns from defaultColumns that weren't in the saved config
          this.defaultColumns.forEach(currentCol => {
              if (!merged.find(col => col.key === currentCol.key)) {
                  merged.push({ ...currentCol });
              }
          });
          
          return merged;
      },

      getCellClass(column, position) {
          const classes = [];
          if (column.numeric) classes.push('number-cell');
          if (column.key === 'ticker') classes.push('ticker-cell');
          if (column.key === 'notes') classes.push('notes-cell');
          if (this.isExternalAnalysisColumn(position, column.key)) classes.push('external-analysis-cell');
          return classes.join(' ');
      },

      isExternalAnalysisColumn(position, key) {
          return this.isExternalPosition(position) && ['support_date', 'tthh_profit'].includes(key);
      },

      renderCell(position, column) {
          switch (column.key) {
              case 'ticker':
                  return this.renderTickerCell(position);
              case 'risk':
                  return this.formatPercentage(position.risk);
              case 'instrument_number':
                  return this.formatNumber(position.instrument_number);
              case 'in_price':
                  if (this.isExternalPosition(position)) return this.formatPrice(position.in_price, position.stock_decimals);
                  return `${this.formatPrice(position.in_price, position.stock_decimals)}<br><small class="current-profitability ${this.getProfitabilityClass(position)}">Rentabilidad actual:<br> ${this.calculateCurrentProfitability(position)}</small>`;
              case 'out_price':
                  return this.formatPrice(position.out_price, position.stock_decimals);
              case 'current_price':
                  return `${this.formatPrice(position.current_price, position.stock_decimals)}${this.getCurrencySymbol(position.currency)}`;
              case 'support_price':
                  return this.formatPrice(position.support_price, position.stock_decimals);
              case 'support_date':
                  if (this.isExternalPosition(position)) return this.externalAnalysisLabel();
                  return this.formatDate(position.support_date);
              case 'in_currency':
                  return this.formatPrice(position.in_currency, 4);
              case 'amount':
                  return this.formatCurrency(position.amount, this.displayPortfolioCurrency);
              case 'benefit':
                  return this.formatCurrency(position.benefit, this.displayPortfolioCurrency);
              case 'insured_benefit':
                  return this.formatCurrency(position.insured_benefit, this.displayPortfolioCurrency);
              case 'tthh_profit':
                  if (this.isExternalPosition(position)) return this.externalAnalysisLabel();
                  return this.formatPercentage(position.tthh_profit);
              case 'cfd_margin':
                  return position.instrument_type === 'CFD' ? (position.cfd_margin !== null && position.cfd_margin !== undefined ? this.formatPrice(position.cfd_margin, 2) : '-') : '-';
              case 'is_ipo':
                  return position.is_ipo == 1 ? 'Sí' : 'No';
              case 'in_date':
                  return this.formatDate(position.in_date);
              case 'open_fee':
                  return `${this.formatPrice(position.open_fee, 2)}${this.getCurrencySymbol(position.currency)}`;
              case 'notes':
                  return this.renderNotesCell(position);
              default:
                  return position[column.key] || '-';
          }
      },

      renderTickerCell(position) {
          const tooltip = this.generateTickerTooltip(position);
          return `
              <div class="ticker-content-wrapper" title="${tooltip}">
                  <div class="ticker-left">
                      <b>${position.ticker}</b>
                      <small>${position.company_name}</small>
                  </div>
                  <div class="ticker-right">
                      ${this.isExternalPosition(position) ? `<span class="external-badge" title="No Eurekers">${this.externalAnalysisLabel()}</span>` : `<span class="direction-icon ${position.direction == 1 ? 'bullish' : 'bearish'} material-icons-outlined" title="${position.direction == 1 ? 'Alcista' : 'Bajista'}">${position.direction == 1 ? 'trending_up' : 'trending_down'}</span>`}
                      <span class="instrument-icon material-icons-outlined" title="${position.instrument_type === 'STOCK' ? 'Acción' : 'CFD'}">${position.instrument_type === 'STOCK' ? 'show_chart' : 'candlestick_chart'}</span>
                      <small class="currency">${position.currency || '-'}</small>
                  </div>
              </div>
          `;
      },

      renderNotesCell(position) {
          if (!position.editingNotes) {
              return `
                  <span>${position.notes || '-'}</span>
                  <button class="btnGcn btnGcn-link btnGcn-sm" onclick="this.closest('tr').querySelector('td').dispatchEvent(new CustomEvent('start-edit-notes', { detail: { id: '${position.id}' } }))" title="Editar Notas">
                      <span class="material-icons-outlined">edit</span>
                  </button>
              `;
          } else {
              return `
                  <textarea class="form-control-gcn form-control-gcn-sm" data-id="${position.id}">${position.editNotesValue}</textarea>
                  <button class="btnGcn btnGcn-link btnGcn-sm" onclick="this.closest('tr').querySelector('td').dispatchEvent(new CustomEvent('save-notes', { detail: { id: '${position.id}' } }))" title="Guardar Notas">
                      <span class="material-icons-outlined">save</span>
                  </button>
              `;
          }
      },
    },
    created() {
      this.initializeColumns();
    },
    watch: {
      portfolioId: {
        immediate: true,
        handler(newId) {
          if (newId) { this.fetchData(); }
          else { 
            this.openPositions = []; 
            this.alerts = [];
            this.error = null; 
            this.alertsError = null;
            this.selectedPositions = []; 
            this.selectedAlerts = [];
            this.alertGroupCollapsedState = {};
          }
        },
      },
    },
  };
  </script>
  
  <style scoped>
  /* Re-adding Material Icons import if it was removed or ensure it's globally available */
  /* @import url('https://fonts.googleapis.com/icon?family=Material+Icons+Outlined'); */

  .posiciones-abiertas-container {
    color: var(--color-text-primary);
    font-size: 0.92rem;
    padding: 0 10px;
  }

  .material-icons-outlined {
    font-size: 16px; /* Default size for icons in text */
    line-height: 1;
    vertical-align: middle;
    margin-right: 3px;
  }
  
  .btnGcn-sm .material-icons-outlined {
    font-size: 16px; /* Ensure icons in small buttons are not too small */
    margin-right: 0; /* No margin if it's just the icon */
  }

  /* --- Custom Button Styles (from ValoresEnEspera) --- */
  .custom-btnGcn {
    background-color: var(--color-highlight);
    color: var(--color-text-on-highlight);
    border: 1px solid var(--color-highlight);
    transition: all 0.2s ease;
    padding: 6px 12px;
    font-size: 0.85rem;
    cursor: pointer;
  }

  .custom-btnGcn:hover {
    background-color: var(--color-text-primary);
    color: var(--color-background);
    border-color: var(--color-text-primary);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px var(--color-outline);
  }

  .custom-btnGcn:active {
    transform: translateY(0);
    box-shadow: 0 1px 4px var(--color-outline);
  }

  .custom-btnGcn:focus {
    outline: none;
    box-shadow: 0 0 0 3px var(--color-active-surface);
  }

  .custom-btnGcn-outline {
    background-color: transparent;
    color: var(--color-highlight);
    border: 1px solid var(--color-outline);
    padding: 6px 12px;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .custom-btnGcn-outline:hover {
    background-color: var(--color-surface);
    color: var(--color-text-primary);
    border-color: var(--color-highlight);
  }

  .custom-btnGcn-outline:active {
    background-color: var(--color-active-surface);
  }

  .custom-btnGcn-outline:focus {
    outline: none;
    box-shadow: 0 0 0 3px var(--color-active-surface);
  }

  .btnGcn-secondary {
    background-color: var(--color-surface);
    color: var(--color-text-primary);
    border: 1px solid var(--color-outline);
    font-size: 0.85em;
    padding: 4px 10px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btnGcn-secondary:hover {
    background-color: var(--color-active-surface);
    border-color: var(--color-text-secondary);
  }

  .btnGcn-secondary:active {
    background-color: var(--color-outline);
  }

  .btnGcn-secondary:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }

  .btnGcn-warning {
    background-color: var(--color-warning);
    color: var(--color-text-primary);
    border: 1px solid var(--color-warning);
    font-size: 0.85em;
    padding: 4px 10px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btnGcn-warning:hover {
    background-color: var(--color-text-primary);
    color: var(--color-background);
    border-color: var(--color-text-primary);
  }

  .btnGcn-warning:active {
    opacity: 0.9;
  }

  .btnGcn-warning:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }

  .custom-btnGcn.btnGcn-warning {
    background-color: var(--color-warning);
    color: var(--color-text-primary);
    border: 1px solid var(--color-warning);
  }

  .custom-btnGcn.btnGcn-warning:hover {
    background-color: var(--color-text-primary);
    color: var(--color-background);
    border-color: var(--color-text-primary);
    transform: translateY(-1px);
  }

  .btnGcn-danger {
    background-color: var(--color-error);
    color: var(--color-text-on-highlight);
    border: 1px solid var(--color-error);
    font-size: 0.85em;
    padding: 4px 10px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btnGcn-danger:hover {
    background-color: var(--color-text-primary);
    color: var(--color-background);
    border-color: var(--color-text-primary);
    transform: translateY(-1px);
  }

  .btnGcn-danger:active {
    transform: translateY(0);
    opacity: 0.9;
  }

  .btnGcn-danger:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }

  /* --- Alerts Styling --- */
  .alerts-section { 
    background-color: var(--color-surface); 
    border: none; 
    border-radius: 6px;
    padding: 12px; 
    margin-bottom: 20px; 
  }
  
  .alerts-header { 
    display: flex; 
    justify-content: space-between; 
    align-items: center; 
    margin-bottom: 12px; 
    padding-bottom: 8px; 
    border-bottom: 1px solid var(--color-outline); 
    flex-wrap: wrap; 
    gap: 8px; 
  }
  
  .alerts-header h3 { 
    margin: 0; 
    font-size: 1.15em; 
    color: var(--color-text-primary); 
    border-bottom: none; 
    padding-bottom: 0; 
    text-shadow: 0 1px 2px var(--color-outline);
  }
  
  .alert-group { 
    border: 1px solid var(--color-outline); 
    border-radius: 5px; 
    margin-bottom: 8px; 
    overflow: hidden; 
    background-color: var(--color-surface); 
    box-shadow: 0 2px 4px var(--color-outline);
    transition: all 0.2s ease; 
  }
  
  .alert-group:hover {
    box-shadow: 0 3px 8px var(--color-outline);
    transform: translateY(-1px);
  }
  
  .alert-group.is-collapsed { 
    background-color: var(--color-background); 
  }
  
  .alert-group h4.collapsible-header { 
    margin: 0; 
    padding: 8px 12px; 
    font-size: 1em; 
    font-weight: 600; 
    background-color: var(--color-surface); 
    border-bottom: 1px solid var(--color-outline); 
    display: flex; 
    justify-content: space-between; 
    align-items: center; 
    cursor: pointer; 
    transition: background-color 0.2s ease; 
    color: var(--color-text-primary);
  }
  
  .alert-group.is-collapsed h4.collapsible-header { 
    border-bottom-color: transparent; 
  }
  
  .alert-group h4.collapsible-header:hover { 
    background-color: var(--color-active-surface); 
  }
  
  .collapse-icon { 
    font-family: monospace; 
    margin-right: 8px; 
    width: 1em; 
    display: inline-block; 
    text-align: center; 
    color: var(--color-text-secondary); 
  }
  
  .alert-group-icon { 
    margin-right: 8px; 
    color: var(--color-highlight);
  }
  
  /* Specific Alert Group Styling */
  .alert-group.alert-type-not-eureker { border-color: var(--color-warning); } 
  .alert-group.alert-type-not-eureker h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-warning); 
  } 
  
  .alert-group.alert-type-support-changed { border-color: var(--color-warning); } 
  .alert-group.alert-type-support-changed h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-warning); 
  } 
  
  .alert-group.alert-type-risk-changed { border-color: var(--color-error); } 
  .alert-group.alert-type-risk-changed h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-error); 
  } 
  
  .alert-group.alert-type-stop-broken { border-color: var(--color-error); } 
  .alert-group.alert-type-stop-broken h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-error); 
  } 
  
  .alert-group.alert-type-max-amount { border-color: var(--color-error); } 
  .alert-group.alert-type-max-amount h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-error); 
  } 
  
  .alert-group.alert-type-entry-price-changed { border-color: var(--color-warning); } 
  .alert-group.alert-type-entry-price-changed h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-warning); 
  } 
  
  .alert-group.alert-type-possible-execution { border-color: var(--color-warning); } 
  .alert-group.alert-type-possible-execution h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-warning); 
  } 
  
  .alert-group.alert-type-default { border-color: var(--color-outline); } 
  .alert-group.alert-type-default h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-text-primary); 
  } 
  
  .alert-group-content { 
    padding: 0; 
    max-height: 300px; /* Limit height for alert tables */
    overflow-y: auto; /* Enable vertical scrolling */
  }
  
  .alert-table { 
    width: 100%; 
    border-collapse: collapse; 
    font-size: 0.85em;
  }
  
  .alert-table th, .alert-table td { 
    padding: 6px 10px; 
    text-align: left; 
    border-bottom: 1px solid var(--color-outline); 
    vertical-align: middle; 
    color: var(--color-text-primary);
  }
  
  .alert-table thead th { 
    background-color: var(--color-surface); 
    font-weight: 600; 
    color: var(--color-text-secondary); 
    border-bottom-width: 2px; 
    font-size: 0.9em;
    padding: 6px 10px;
    position: sticky; /* Make header sticky */
    top: 0; /* Stick to top when scrolling */
    z-index: 10; /* Ensure header stays above content */
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Add shadow for better visibility */
    border-bottom: 2px solid var(--color-outline); /* Stronger border for sticky header */
  }
  
  .alert-table tbody tr:last-child td { 
    border-bottom: none; 
  }
  
  .alert-table td.alert-actions { 
    white-space: nowrap; 
    text-align: right; 
  }
  
  .alert-table .btnGcn { 
    margin-left: 4px; 
    padding: 2px 8px;
    font-size: 0.85em;
  }

  .alert-group-bulk-actions select {
    padding: 2px 4px;
    font-size: 0.8em;
    height: auto;
    border-radius: 3px;
    border: 1px solid var(--color-outline);
    background-color: var(--color-surface);
    color: var(--color-text-secondary);
    width: 110px;
    text-overflow: ellipsis;
  }

  .btnGcn-success {
    background-color: var(--color-success);
    color: var(--color-text-on-highlight);
    border: 1px solid var(--color-success);
    font-size: 0.85em;
    padding: 4px 10px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btnGcn-success:hover {
    background-color: var(--color-text-primary);
    color: var(--color-background);
    border-color: var(--color-text-primary);
    transform: translateY(-1px);
  }

  .btnGcn-success:active {
    transform: translateY(0);
    opacity: 0.9;
  }

  .btnGcn-success:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }

  .loading-indicator-gcn, .error-message-gcn {
    color: var(--color-text-secondary);
    text-align: center;
    padding: 15px;
    font-size: 0.9em;
    border-radius: 4px;
    margin-bottom: 12px;
  }

  .error-message-gcn {
    color: var(--color-error);
    background-color: var(--color-active-surface);
  }
  
  /* --- Open Positions List Styling (Adapted from ValoresEnEspera) --- */
  .list-actions { 
    display: flex; 
    justify-content: space-between; 
    align-items: center; 
    margin-bottom: 15px; 
    flex-wrap: wrap; 
    gap: 8px; 
    padding: 8px 12px;
    border-radius: 5px;
    background: linear-gradient(to right, var(--color-active-surface), var(--color-background));
  }

  .list-actions-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .list-actions-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
  
  .bulk-actions { 
    display: flex; 
    align-items: center; 
    gap: 5px; 
  }
  
  .bulk-actions select { 
    padding: 3px 6px; 
    font-size: 0.85em; 
    height: auto; 
    border-radius: 4px; 
    border: 1px solid var(--color-outline);
    background-color: var(--color-surface);
    color: var(--color-text-primary);
  }
  .bulk-actions .btnGcn-sm {
    padding: 4px 10px; /* Consistent padding for small buttons */
  }
  
  .table-responsive { 
    overflow-x: auto; 
    margin-top: 12px; 
    border-radius: 4px;
    box-shadow: 0 2px 8px var(--color-outline);
    margin-bottom: 20px;
    max-height: 70vh; /* Limit height to enable scrolling */
    overflow-y: auto; /* Enable vertical scrolling */
  }
  
  .table.data-table { 
    width: 100%; 
    border-collapse: collapse; 
    font-size: 0.85em; 
  }
  
  .data-table th, .data-table td { 
    border: 1px solid var(--color-outline);
    padding: 6px 8px; 
    text-align: center; 
    vertical-align: middle; 
    color: var(--color-text-primary);
    line-height: 1.3;
  }
  
  .data-table th { 
    background-color: var(--color-surface); 
    font-weight: 600; 
    white-space: pre-line; 
    color: var(--color-text-secondary);
    text-align: center; 
    font-size: 0.9em;
    padding: 6px 8px;
    line-height: 1.2;
    position: sticky; /* Make header sticky */
    top: 0; /* Stick to top when scrolling */
    z-index: 10; /* Ensure header stays above content */
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Add shadow for better visibility */
    border-bottom: 2px solid var(--color-outline); /* Stronger border for sticky header */
  }
  
  .data-table tbody tr:hover { 
    background-color: var(--color-active-surface); 
  }
  
  .direction-icon, .instrument-icon { 
    font-size: 1.1em; 
    display: inline-block; 
    text-align: center; 
    width: 18px; 
  }
  
  .direction-icon.bullish { 
    color: var(--color-success); 
    text-shadow: 0 0 6px var(--color-success);
  }
  
  .direction-icon.bearish { 
    color: var(--color-error); 
    text-shadow: 0 0 6px var(--color-error);
  }
  .external-badge {
    display: block;
    font-size: 0.65em;
    font-weight: 700;
    line-height: 1.1;
    color: var(--color-text-secondary);
    text-align: right;
    max-width: 70px;
  }
  .data-table td.external-analysis-cell {
    background-color: rgba(128, 128, 128, 0.14);
    color: var(--color-text-secondary);
    font-weight: 600;
  }
  .instrument-icon {
    color: var(--color-text-secondary);
  }
  
  .ticker-cell { 
    font-weight: 600; 
    vertical-align: top;
    text-align: left;
  }

  /* Override centering specifically for ticker cells in table body */
  .data-table tbody .ticker-cell {
    text-align: left;
  }

  .ticker-content-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center; 
    width: 100%;
  }
  .ticker-left {
    flex-grow: 1; 
    margin-right: 8px; 
  }
  .ticker-left b {
    display: block; 
  }
  .ticker-left small {
    display: block; 
    color: var(--color-text-secondary); 
    font-size: 0.85em; 
    font-weight: 400;
    line-height: 1.2;
  }
  .ticker-right {
    display: flex;
    flex-direction: column; 
    align-items: flex-end; 
    flex-shrink: 0; 
    text-align: right;
  }
  .ticker-right .direction-icon,
  .ticker-right .instrument-icon {
    display: block; 
    margin-bottom: 1px; 
    width: auto; 
    text-align: right;
  }
  .ticker-right .instrument-icon {
    font-size: 1em; 
  }
  .ticker-right small.currency {
    font-size: 0.8em;
    color: var(--color-text-secondary);
    margin-top: 2px;
  }

  .notes-cell { 
    min-width: 150px; 
    white-space: pre-wrap; 
    font-size: 0.9em;
  }
  
  .notes-cell textarea { 
    width: 100%; 
    min-height: 50px; 
    font-size: 0.9em; 
    padding: 4px; 
    box-sizing: border-box; 
    border-radius: 3px; 
    border: 1px solid var(--color-outline);
    background-color: var(--color-surface);
    color: var(--color-text-primary);
  }
  
  .notes-cell textarea:focus {
    border-color: var(--color-highlight);
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }
  
  .notes-cell .btnGcn-link {
    padding: 0;
    width: 20px;
    height: 20px;
    font-size: 14px;
    vertical-align: top; 
    color: var(--color-text-secondary);
    background-color: transparent;
    border: none;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 4px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .notes-cell .btnGcn-link:hover {
    color: var(--color-text-primary);
    background-color: var(--color-surface);
    transform: scale(1.1);
  }

  .notes-cell .btnGcn-link:active {
    transform: scale(1.05);
    background-color: var(--color-active-surface);
  }

  .notes-cell .btnGcn-link .material-icons-outlined {
    margin-right: 0;
  }
  
  .options-cell { 
    white-space: nowrap; 
    text-align: center; 
  }
  
  .options-cell .btnGcn-link { 
    padding: 0; 
    width: 24px;
    height: 24px;
    font-size: 16px; 
    text-decoration: none; 
    vertical-align: middle; 
    color: var(--color-text-secondary); 
    background-color: transparent;
    border: none;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 1px;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  
  .options-cell .btnGcn-link:hover { 
    color: var(--color-text-primary); 
    background-color: var(--color-surface);
    transform: scale(1.1);
  }

  .options-cell .btnGcn-link:active {
    transform: scale(1.05);
    background-color: var(--color-active-surface);
  }

  .options-cell .btnGcn-link:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }
  
  .options-cell .btnGcn-link.btnGcn-warning {
    color: var(--color-warning); 
  } 

  .options-cell .btnGcn-link.btnGcn-warning:hover {
    color: var(--color-text-on-highlight);
    background-color: var(--color-warning);
  }
  
  .options-cell .btnGcn-link.btnGcn-danger { 
    color: var(--color-error); 
  }

  .options-cell .btnGcn-link.btnGcn-danger:hover {
    color: var(--color-text-on-highlight);
    background-color: var(--color-error);
  }

  .options-cell .btnGcn-link.btnGcn-chart {
    color: var(--color-highlight);
  }

  .options-cell .btnGcn-link.btnGcn-chart:hover {
    color: var(--color-text-on-highlight);
    background-color: var(--color-highlight);
  }

  .options-cell .btnGcn-link.btnGcn-graph {
    color: var(--color-highlight);
  }

  .options-cell .btnGcn-link.btnGcn-graph:hover {
    color: var(--color-text-on-highlight);
    background-color: var(--color-highlight);
  }
  
  .options-cell .btnGcn-link.disabled {
    color: var(--color-text-inactive); 
    cursor: not-allowed;
    pointer-events: none; 
    opacity: 0.3; 
    transition: none;
  }

  .options-cell .btnGcn-link.disabled:hover,
  .options-cell .btnGcn-link.disabled:focus,
  .options-cell .btnGcn-link.disabled:active {
    background-color: transparent !important;
    color: var(--color-text-inactive) !important;
    transform: none !important;
    box-shadow: none !important;
    opacity: 0.3 !important;
  }

  /* Disabled state for all button types */
  .custom-btnGcn:disabled,
  .custom-btnGcn-outline:disabled,
  .btnGcn-secondary:disabled,
  .btnGcn-warning:disabled,
  .btnGcn-danger:disabled {
    background-color: var(--color-surface);
    color: var(--color-text-inactive);
    border-color: var(--color-outline);
    cursor: not-allowed;
    opacity: 0.5;
    transform: none !important;
    box-shadow: none !important;
    pointer-events: none;
  }

  .text-success { 
    color: var(--color-success); 
    font-weight: 500; 
  }

  .text-danger { 
    color: var(--color-error); 
    font-weight: 500; 
  }

  td.text-success small, td.text-danger small { 
    font-weight: normal; 
    color: var(--color-text-secondary); 
  }
  
  .data-table td.number-cell {
    text-align: center;
    padding-right: 8px;
  }

  .data-table th.number-cell {
    text-align: center;
  }

  .current-price-info {
    display: block;
    color: var(--color-text-secondary);
    font-weight: 400;
    font-size: 0.8em;
    margin-top: 2px;
  }

  .current-profitability {
    display: block;
    font-weight: 500;
    font-size: 0.8em;
    margin-top: 2px;
  }

  .current-profitability.text-success {
    color: var(--color-success);
  }

  .current-profitability.text-danger {
    color: var(--color-error);
  }

  /* Form styling within modals - REMOVE CONFLICTING STYLES */
  .modal-body-gcn .form-group-gcn {
      margin-bottom: 15px;
  }

  .modal-body-gcn label {
      display: block;
      margin-bottom: 5px;
      font-weight: 500;
      font-size: 0.9em;
      color: var(--color-text-secondary);
  }

  /* REMOVE THIS CONFLICTING BLOCK */

  /* REMOVE THIS CONFLICTING BLOCK */

  .modal-body-gcn .form-control-gcn {
      /* Use global form styles - match styles.css */
      display: block;
      width: 100%;
      padding: 12px 14px;
      font-size: 1em;
      line-height: 1.5;
      color: var(--color-text-primary);
      background-color: var(--color-background);
      background-clip: padding-box;
      border: 1px solid var(--color-outline);
      border-radius: 8px;
      transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
      box-sizing: border-box;
  }

  .modal-body-gcn .form-control-gcn:focus {
    color: var(--color-text-primary);
    background-color: var(--color-background);
    border-color: var(--color-highlight);
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 173, 217, 0.25);
  }

  /* Form controls outside modals */
  .form-control-gcn {
    background-color: var(--color-surface);
    border: 1px solid var(--color-outline);
    color: var(--color-text-primary);
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 0.9em;
  }

  .form-control-gcn:focus {
    border-color: var(--color-highlight);
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }

  /* Form validation styles for edit modal (copied from PortfolioForm.vue) */
  .required {
    color: var(--color-error);
    font-weight: bold;
  }

  .error-message-gcn {
    color: var(--color-error);
    font-size: 0.8em;
    display: block;
    margin-top: 4px;
  }

  .text-primary {
    color: var(--color-text-primary);
  }

  .text-secondary {
    color: var(--color-text-secondary);
  }

  .form-control-gcn.error,
  .modal-body-gcn .form-control-gcn.error {
    border-color: var(--color-error) !important;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
  }

  .input-feedback {
    margin-top: 4px;
  }

  .character-count {
    float: right;
    font-size: 0.75em;
  }

  .form-status {
    padding: 10px;
    border-radius: 4px;
    margin-top: 15px;
    font-size: 0.9em;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .form-status.success {
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
    color: #155724;
  }

  .form-status.error {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
  }

  .status-icon {
    font-weight: bold;
    font-size: 1.1em;
  }

  /* Use global form styles - just ensure they work in modal */
  .modal-body-gcn .form-control-gcn {
    /* Let global styles apply, just ensure box-sizing */
    box-sizing: border-box !important;
  }

  /* Additional form styling for better visual hierarchy */
  .modal-body-gcn label.text-primary {
    color: var(--color-text-primary) !important;
    font-weight: 600 !important;
  }

  .modal-body-gcn .required {
    margin-left: 2px !important;
  }

  /* Exchange Rate Button Styles */
  .exchange-rate-btn {
    margin-top: 8px;
    white-space: nowrap;
    font-size: 0.8rem;
    padding: 6px 12px;
    transition: all 0.2s ease;
    align-self: flex-start;
  }

  .exchange-rate-btn:hover:not(:disabled) {
    background-color: var(--color-highlight);
    color: var(--color-text-on-highlight);
    border-color: var(--color-highlight);
  }

  .exchange-rate-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .exchange-rate-success {
    color: var(--color-success) !important;
    font-weight: 500;
  }

  /* Small screen adjustments */
  @media (max-width: 768px) {
    .data-table th, .data-table td {
      padding: 4px 6px;
      font-size: 0.8em;
    }

    .options-cell .btnGcn-link {
      width: 22px;
      height: 22px;
      font-size: 14px;
    }

    .list-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .list-actions .btnGcn {
      width: 100%;
      margin-bottom: 5px;
    }

    .bulk-actions {
      width: 100%;
      justify-content: space-between;
    }

    .bulk-actions select {
      flex-grow: 1;
    }
    
    /* Adjust sticky header behavior on mobile */
    .data-table th {
      position: sticky;
      top: 0;
      z-index: 10;
    }
    
    .alert-table thead th {
      position: sticky;
      top: 0;
      z-index: 10;
    }
    
    /* Reduce max height on mobile for better usability */
    .table-responsive {
      max-height: 60vh;
    }
    
    .alert-group-content {
      max-height: 250px;
    }
  }

  /* Sortable Header Styles */
  .sortable-header {
    cursor: pointer;
    user-select: none;
    position: relative;
    transition: background-color 0.2s ease;
  }

  .sortable-header:hover {
    background-color: var(--color-active-surface) !important;
  }

  .sortable-header:active {
    background-color: var(--color-outline) !important;
  }

  .sort-icon {
    margin-left: 6px;
    font-size: 0.75em;
    opacity: 0.3;
    transition: opacity 0.2s ease;
    font-weight: normal;
    color: var(--color-text-secondary);
    vertical-align: baseline;
    display: inline-block;
    line-height: 1;
    position: relative;
    top: -1px;
  }

  .sortable-header:hover .sort-icon {
    opacity: 0.6;
  }

  .sort-icon.sort-asc {
    opacity: 1;
    color: var(--color-highlight);
    font-weight: 600;
  }

  .sort-icon.sort-asc::before {
    content: '▲';
  }

  .sort-icon.sort-desc {
    opacity: 1;
    color: var(--color-highlight);
    font-weight: 600;
  }

  .sort-icon.sort-desc::before {
    content: '▼';
  }

  .sort-icon.sort-none::before {
    content: '◆';
  }

  /* Edit Mode Selector Styles */
  .edit-mode-selector {
    margin-bottom: 20px;
    margin-top: 20px;
    padding: 16px;
    background-color: var(--color-surface);
    border-radius: 8px;
    border: 1px solid var(--color-outline);
  }

  .edit-mode-title {
    margin: 0 0 12px 0;
    font-size: 0.95em;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .edit-mode-options {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .edit-mode-option {
    flex: 1;
    min-width: 180px;
    cursor: pointer;
    padding: 12px;
    border: 2px solid var(--color-outline);
    border-radius: 6px;
    background-color: var(--color-background);
    transition: all 0.2s ease;
    position: relative;
  }

  .edit-mode-option:hover {
    border-color: var(--color-highlight);
    background-color: var(--color-active-surface);
  }

  .edit-mode-option.active {
    border-color: var(--color-highlight);
    background-color: var(--color-active-surface);
    box-shadow: 0 0 0 1px var(--color-highlight);
  }

  .edit-mode-option input[type="radio"] {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  .mode-content {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .mode-icon {
    font-size: 1.2em;
    line-height: 1;
  }

  .mode-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .mode-text strong {
    font-size: 0.9em;
    color: var(--color-text-primary);
    font-weight: 600;
  }

  .mode-text small {
    font-size: 0.8em;
    color: var(--color-text-secondary);
    line-height: 1.2;
  }
</style>
